<template>
    <div class="page orders-list-page">
        <div class="page-header orders-page-header">
            <div>
                <h1>訂單管理</h1>
                <p>訂單流程處理：查核付款、標記付款/完成、取消與退款</p>
            </div>
            <a-button
                v-if="canExportOrders"
                class="unified-secondary-btn"
                :loading="exportLoading"
                :disabled="exportLoading"
                @click="handleExport"
            >
                匯出 Excel
            </a-button>
        </div>

        <div class="content light-theme">
            <div class="order-quick-status">
                <span class="order-quick-label">快速篩選</span>
                <a-button
                    v-for="item in QUICK_STATUS_OPTIONS"
                    :key="item.label"
                    size="small"
                    :type="
                        filters.status === item.value ? 'primary' : 'default'
                    "
                    @click="applyQuickStatus(item.value)"
                >
                    {{ item.label }}
                </a-button>
            </div>

            <!-- 搜尋與篩選區域（預設收合） -->
            <div class="search-filter-section">
                <a-collapse
                    v-model:activeKey="filterCollapseActiveKey"
                    :bordered="false"
                    class="orders-filter-collapse"
                >
                    <a-collapse-panel key="filters">
                        <template #header>
                            <span class="orders-filter-header-title">
                                篩選條件
                            </span>
                        </template>
                        <div class="orders-filter-body">
                            <a-row :gutter="[16, 12]">
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        訂單編號
                                    </label>
                                    <a-input
                                        v-model:value="filters.order_no"
                                        placeholder="輸入訂單編號"
                                        size="middle"
                                        class="w-full order-no-input"
                                        allow-clear
                                        @press-enter="handleSearch"
                                    />
                                </a-col>
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        會員
                                    </label>
                                    <a-select
                                        v-model:value="filters.user_id"
                                        :options="userOptions"
                                        :loading="userLoading"
                                        placeholder="搜尋會員姓名"
                                        size="middle"
                                        class="status-select w-full"
                                        show-search
                                        allow-clear
                                        :filter-option="false"
                                        @search="onUserSearch"
                                        @change="handleUserSelect"
                                    />
                                </a-col>
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        商品
                                    </label>
                                    <a-select
                                        v-model:value="filters.product_id"
                                        :options="productOptions"
                                        :loading="productLoading"
                                        placeholder="搜尋商品名稱"
                                        size="middle"
                                        class="status-select w-full"
                                        show-search
                                        allow-clear
                                        :filter-option="false"
                                        @search="onProductSearch"
                                    />
                                </a-col>
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        訂單狀態
                                    </label>
                                    <a-select
                                        v-model:value="filters.status"
                                        :options="statusOptions"
                                        placeholder="全部狀態"
                                        size="middle"
                                        class="status-select w-full"
                                        allow-clear
                                    />
                                </a-col>
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        收款帳號
                                    </label>
                                    <a-select
                                        v-model:value="filters.payment_account_key"
                                        :options="paymentAccountOptions"
                                        placeholder="全部帳號"
                                        size="middle"
                                        class="status-select w-full"
                                        allow-clear
                                    />
                                </a-col>
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        下單日期
                                    </label>
                                    <a-range-picker
                                        v-model:value="dateRange"
                                        :placeholder="['起日', '迄日']"
                                        size="middle"
                                        class="w-full"
                                        @change="handleDateRangeChange"
                                    />
                                </a-col>
                                <a-col :xs="24" :sm="12" :md="8" :lg="6">
                                    <label class="orders-filter-label">
                                        金額範圍
                                    </label>
                                    <div class="amount-range-inputs">
                                        <a-input-number
                                            v-model:value="filters.min_amount"
                                            placeholder="最小"
                                            size="middle"
                                            :min="0"
                                            :precision="0"
                                            class="amount-input"
                                        />
                                        <span class="amount-separator">~</span>
                                        <a-input-number
                                            v-model:value="filters.max_amount"
                                            placeholder="最大"
                                            size="middle"
                                            :min="0"
                                            :precision="0"
                                            class="amount-input"
                                        />
                                    </div>
                                </a-col>
                            </a-row>
                            <div class="orders-filter-actions">
                                <a-space wrap>
                                    <a-button
                                        size="middle"
                                        class="unified-search-btn"
                                        @click="handleSearch"
                                    >
                                        查詢
                                    </a-button>
                                    <a-button
                                        size="middle"
                                        class="unified-reset-btn"
                                        @click="handleReset"
                                    >
                                        重置
                                    </a-button>
                                    <a-button
                                        type="link"
                                        class="orders-column-settings-btn"
                                        @click="showColumnSettingsModal"
                                    >
                                        欄位設置
                                    </a-button>
                                </a-space>
                            </div>
                        </div>
                    </a-collapse-panel>
                </a-collapse>
            </div>

            <!-- 訂單列表表格 -->
            <div class="page-table-section">
                <DataTable
                    title=""
                    :columns="columns"
                    :data-source="orders"
                    :pagination="pagination"
                    :loading="loading"
                    :no-card="true"
                    :scroll="{ x: 1180 }"
                    @refresh="loadOrders"
                    @change="handleTableChange"
                >
                    <template #bodyCell="{ column, record }">
                        <!-- 訂單編號（可點擊開啟詳情） -->
                        <template v-if="column.key === 'order_no'">
                            <a
                                v-if="canViewOrder"
                                class="order-no-link"
                                @click.stop="handleViewDetail(record)"
                            >
                                {{ record.order_no }}
                            </a>
                            <span v-else class="order-no-text">
                                {{ record.order_no }}
                            </span>
                        </template>

                        <!-- 付款單號（空顯示 -，附複製圖示） -->
                        <template v-if="column.key === 'payment_no'">
                            <span class="payment-no-cell">
                                {{ record.payment_no || '-' }}
                                <a-tooltip
                                    v-if="record.payment_no"
                                    title="複製付款單號"
                                >
                                    <CopyOutlined
                                        class="payment-no-copy-icon"
                                        @click.stop="
                                            copyPaymentNo(record.payment_no)
                                        "
                                    />
                                </a-tooltip>
                            </span>
                        </template>

                        <!-- 會員（可點擊跳轉；推薦人次要資訊收在同一欄） -->
                        <template v-if="column.key === 'user'">
                            <div class="user-cell">
                                <a
                                    v-if="record.user?.id && canViewMember"
                                    class="user-link"
                                    @click.stop="handleViewMember(record.user.id)"
                                >
                                    {{ record.user?.name || '-' }}
                                </a>
                                <span v-else class="user-name">
                                    {{ record.user?.name || '-' }}
                                </span>
                                <div
                                    v-if="record.referrer?.name"
                                    class="user-referrer-line"
                                >
                                    推薦
                                    <a
                                        v-if="canViewMember && record.referrer?.id"
                                        class="user-link user-link--sub"
                                        @click.stop="
                                            handleViewMember(record.referrer.id)
                                        "
                                    >
                                        {{ record.referrer.name }}
                                    </a>
                                    <span v-else>{{
                                        record.referrer.name
                                    }}</span>
                                </div>
                            </div>
                        </template>

                        <!-- 推薦人 -->
                        <template v-if="column.key === 'referrer'">
                            <a
                                v-if="canViewMember && record.referrer?.id"
                                class="user-link"
                                @click.stop="handleViewMember(record.referrer.id)"
                            >
                                {{ record.referrer.name }}
                            </a>
                            <span v-else class="user-name">
                                {{ record.referrer?.name || '—' }}
                            </span>
                        </template>

                        <!-- 付款方式＋收款帳號（短標）＋匯款資料狀態 icon -->
                        <template v-if="column.key === 'payment'">
                            <div class="payment-cell">
                                <!-- 銀行匯款：一定走 gateway 分支，才能掛上匯款 icon -->
                                <template
                                    v-if="
                                        getOrderPaymentDisplay(record)
                                            .gateway === 'bank_transfer'
                                    "
                                >
                                    <a-tag
                                        :color="
                                            getPaymentGatewayTagColor(
                                                getOrderPaymentDisplay(record)
                                                    .gateway
                                            )
                                        "
                                        size="small"
                                        class="payment-gateway-tag-with-icon"
                                    >
                                        {{
                                            getPaymentGatewayText(
                                                getOrderPaymentDisplay(record)
                                                    .gateway
                                            )
                                        }}
                                        <CheckCircleOutlined
                                            v-if="hasBankTransferInfo(record)"
                                            class="bank-transfer-icon bank-transfer-icon-ok"
                                        />
                                        <CloseCircleOutlined
                                            v-else
                                            class="bank-transfer-icon bank-transfer-icon-missing"
                                        />
                                    </a-tag>
                                </template>
                                <!-- 其他付款方式：維持原本顯示 -->
                                <template v-else>
                                    <span
                                        v-if="
                                            getOrderPaymentDisplay(record)
                                                .method
                                        "
                                    >
                                        <a-tag
                                            :color="
                                                getPaymentMethodTagColor(
                                                    getOrderPaymentDisplay(
                                                        record
                                                    ).method
                                                )
                                            "
                                            size="small"
                                        >
                                            {{
                                                getPaymentMethodText(
                                                    getOrderPaymentDisplay(
                                                        record
                                                    ).method
                                                )
                                            }}
                                        </a-tag>
                                    </span>
                                    <span
                                        v-else-if="
                                            getOrderPaymentDisplay(record)
                                                .gateway
                                        "
                                    >
                                        <a-tag
                                            :color="
                                                getPaymentGatewayTagColor(
                                                    getOrderPaymentDisplay(
                                                        record
                                                    ).gateway
                                                )
                                            "
                                            size="small"
                                        >
                                            {{
                                                getPaymentGatewayText(
                                                    getOrderPaymentDisplay(
                                                        record
                                                    ).gateway
                                                )
                                            }}
                                        </a-tag>
                                    </span>
                                    <span v-else class="payment-empty">-</span>
                                </template>
                                <span
                                    v-if="
                                        getPaymentAccountText(record) &&
                                        getPaymentAccountText(record) !== '-'
                                    "
                                    class="payment-account-inline"
                                >
                                    {{ getPaymentAccountText(record) }}
                                </span>
                            </div>
                        </template>

                        <!-- 狀態（突出顯示） -->
                        <template v-if="column.key === 'status'">
                            <div class="status-cell">
                                <div
                                    :class="[
                                        'status-badge',
                                        `status-badge-${getStatusTagClass(record.status)}`
                                    ]"
                                >
                                    <span class="status-text">
                                        {{ getStatusText(record.status) }}
                                    </span>
                                </div>
                            </div>
                        </template>

                        <!-- 匯款銀行 -->
                        <template v-if="column.key === 'transfer_bank'">
                            <template
                                v-if="
                                    getOrderPaymentDisplay(record).gateway ===
                                    'bank_transfer'
                                "
                            >
                                {{ record.transfer_bank || '-' }}
                            </template>
                            <span v-else class="payment-empty">-</span>
                        </template>

                        <!-- 匯款戶名 -->
                        <template v-if="column.key === 'account_name'">
                            <template
                                v-if="
                                    getOrderPaymentDisplay(record).gateway ===
                                    'bank_transfer'
                                "
                            >
                                {{ record.account_name || '-' }}
                            </template>
                            <span v-else class="payment-empty">-</span>
                        </template>

                        <!-- 匯款帳號後五碼 -->
                        <template v-if="column.key === 'account_last_5'">
                            <template
                                v-if="
                                    getOrderPaymentDisplay(record).gateway ===
                                    'bank_transfer'
                                "
                            >
                                {{ record.account_last_5 || '-' }}
                            </template>
                            <span v-else class="payment-empty">-</span>
                        </template>

                        <!-- 訂單金額 -->
                        <template v-if="column.key === 'amount'">
                            <div class="order-amount-cell">
                                <span class="amount-value">
                                    {{ formatAmount(record.grand_total) }}
                                </span>
                                <span
                                    v-if="getActualRefundAmount(record) > 0"
                                    class="amount-refund"
                                >
                                    退
                                    {{
                                        formatAmount(
                                            getActualRefundAmount(record)
                                        )
                                    }}
                                </span>
                            </div>
                        </template>

                        <!-- 商品列表 -->
                        <template v-if="column.key === 'items'">
                            <div class="items-list">
                                <div
                                    v-for="item in record.items?.slice(0, 2)"
                                    :key="item.id"
                                    class="item-row"
                                >
                                    <span
                                        class="item-type-tag"
                                        :class="
                                            getItemTypeClass(item.type_snapshot)
                                        "
                                    >
                                        {{
                                            getItemTypeLabel(item.type_snapshot)
                                        }}
                                    </span>
                                    <span
                                        class="item-name"
                                        :title="item.name_snapshot"
                                    >
                                        {{ item.name_snapshot }}
                                    </span>
                                    <span class="item-qty">
                                        ×{{ item.qty }}
                                    </span>
                                </div>
                                <div
                                    v-if="record.items?.length > 2"
                                    class="item-more"
                                    :title="
                                        record.items
                                            .slice(2)
                                            .map(
                                                (item) =>
                                                    `${getItemTypeLabel(item.type_snapshot)} ${item.name_snapshot} ×${item.qty}`
                                            )
                                            .join('\n')
                                    "
                                >
                                    +{{ record.items.length - 2 }} 項
                                </div>
                                <div
                                    v-if="
                                        !record.items ||
                                        record.items.length === 0
                                    "
                                    class="item-empty"
                                >
                                    無商品
                                </div>
                            </div>
                        </template>

                        <!-- 結帳發票（可換行） -->
                        <template v-if="column.key === 'user_invoice'">
                            <template
                                v-for="inv in [getOrderCheckoutInvoice(record)]"
                                :key="
                                    inv.type +
                                    (inv.vatNumber || inv.carrierCode || 'p')
                                "
                            >
                                <div class="user-invoice-cell">
                                    <span
                                        class="user-invoice-type"
                                        :class="{
                                            'user-invoice-type--company':
                                                inv.type === 'company'
                                        }"
                                    >
                                        {{ inv.typeLabel }}
                                    </span>
                                    <template v-if="inv.type === 'company'">
                                        <div class="user-invoice-line">
                                            統編 {{ inv.vatNumber }}
                                        </div>
                                        <div
                                            v-if="inv.title"
                                            class="user-invoice-line user-invoice-line--wrap"
                                        >
                                            {{ inv.title }}
                                        </div>
                                    </template>
                                    <div
                                        v-else-if="
                                            inv.type === 'carrier' ||
                                            inv.type === 'donation'
                                        "
                                        class="user-invoice-line user-invoice-line--wrap"
                                        :class="{
                                            'user-invoice-line--mono':
                                                inv.type === 'carrier'
                                        }"
                                    >
                                        {{ inv.detailLabel }}
                                        {{ inv.detailValue }}
                                    </div>
                                    <div
                                        v-else
                                        class="user-invoice-line user-invoice-line--muted"
                                    >
                                        個人發票
                                    </div>
                                </div>
                            </template>
                        </template>

                        <!-- 下單／付款時間 -->
                        <template v-if="column.key === 'order_times'">
                            <div class="time-cell">
                                <div class="time-row">
                                    <span class="time-label">下單</span>
                                    <span>{{
                                        formatDateTime(record.placed_at, {
                                            showSeconds: false
                                        }) || '—'
                                    }}</span>
                                </div>
                                <div class="time-row">
                                    <span class="time-label">付款</span>
                                    <span>{{
                                        record.paid_at
                                            ? formatDateTime(record.paid_at, {
                                                  showSeconds: false
                                              })
                                            : '—'
                                    }}</span>
                                </div>
                            </div>
                        </template>

                        <!-- 操作按鈕 -->
                        <template v-if="column.key === 'actions'">
                            <div class="action-buttons">
                                <a-button
                                    v-if="canViewOrder"
                                    size="small"
                                    class="action-btn-view"
                                    @click="handleViewDetail(record)"
                                >
                                    詳情
                                </a-button>
                                <a-dropdown
                                    v-if="hasOrderMenuItems(record)"
                                    :trigger="['click']"
                                >
                                    <a-button
                                        size="small"
                                        class="action-btn-more"
                                    >
                                        操作
                                        <DownOutlined />
                                    </a-button>
                                    <template #overlay>
                                        <a-menu
                                            @click="
                                                (event) =>
                                                    handleOrderMenuClick(
                                                        event,
                                                        record
                                                    )
                                            "
                                        >
                                            <a-menu-item
                                                v-if="
                                                    record.status ===
                                                        'pending' &&
                                                    canModifyStatus
                                                "
                                                key="mark-paid"
                                            >
                                                標記付款
                                            </a-menu-item>
                                            <a-menu-item
                                                v-if="
                                                    record.status ===
                                                        'pending' &&
                                                    canCancelOrder
                                                "
                                                key="cancel"
                                                danger
                                            >
                                                取消訂單
                                            </a-menu-item>
                                            <a-menu-item
                                                v-if="
                                                    record.status === 'paid' &&
                                                    canCompleteOrder
                                                "
                                                key="complete"
                                            >
                                                標記完成
                                            </a-menu-item>
                                            <a-menu-item
                                                v-if="
                                                    (record.status === 'paid' ||
                                                        record.status ===
                                                            'refunding' ||
                                                        record.status ===
                                                            'refund_failed') &&
                                                    canRefundOrder
                                                "
                                                key="refund"
                                                danger
                                            >
                                                退款
                                            </a-menu-item>
                                            <a-menu-item
                                                v-if="
                                                    canShowOrderStatusLog(
                                                        record
                                                    )
                                                "
                                                key="logs"
                                            >
                                                狀態歷程
                                            </a-menu-item>
                                        </a-menu>
                                    </template>
                                </a-dropdown>
                            </div>
                        </template>
                    </template>
                </DataTable>
            </div>
        </div>

        <!-- 欄位設置 Modal -->
        <a-modal
            v-model:open="columnSettingsModalVisible"
            title="欄位顯示設置"
            width="500px"
        >
            <div class="column-settings-content">
                <p class="column-settings-hint">選擇要在表格中顯示的欄位</p>
                <a-checkbox-group
                    v-model:value="visibleColumns"
                    class="column-checkbox-group"
                >
                    <a-row :gutter="[16, 12]">
                        <a-col
                            :span="12"
                            v-for="col in allColumns"
                            :key="col.key"
                        >
                            <a-checkbox
                                :value="col.key"
                                :disabled="col.key === 'actions'"
                            >
                                {{ col.title }}
                                <span
                                    v-if="col.key === 'actions'"
                                    class="required-badge"
                                >
                                    （必選）
                                </span>
                            </a-checkbox>
                        </a-col>
                    </a-row>
                </a-checkbox-group>
            </div>
            <template #footer>
                <a-button @click="columnSettingsModalVisible = false">
                    取消
                </a-button>
                <a-button type="primary" @click="saveColumnSettings">
                    確定
                </a-button>
            </template>
        </a-modal>

        <!-- 訂單詳情 Modal -->
        <DetailModal
            v-model:open="detailModalVisible"
            title="訂單詳情資料"
            :width="900"
            :data="selectedOrder"
        >
            <template #content>
                <OrderDetailContent
                    :data="selectedOrder"
                    :loading="loading"
                    :can-edit-bank="canModifyStatus"
                    @edit-bank="openBankTransferEdit"
                    @edit-invoice="openInvoiceEdit"
                />
            </template>
        </DetailModal>

        <!-- 標記已付款 Modal -->
        <a-modal
            v-model:open="paidModalVisible"
            title="標記已付款"
            :width="600"
            :confirm-loading="paidLoading"
            @ok="handleConfirmPaid"
            @cancel="paidModalVisible = false"
        >
            <div v-if="paidOrderData" class="paid-modal-content">
                <!-- 訂單資訊 -->
                <div class="paid-order-info">
                    <h3 class="paid-section-title">訂單資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="訂單編號">
                            {{ paidOrderData.order_no }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單狀態">
                            {{ getStatusText(paidOrderData.status) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="商品小計">
                            {{ formatAmount(paidOrderData.subtotal) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="paidOrderData.shipping_fee > 0"
                            label="運費"
                        >
                            {{ formatAmount(paidOrderData.shipping_fee) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="paidOrderData.point_discount_total > 0"
                            label="積分抵扣"
                            :span="2"
                        >
                            <div class="point-discount-block">
                                <span class="point-discount">
                                    -{{
                                        formatAmount(
                                            paidOrderData.point_discount_total
                                        )
                                    }}
                                </span>
                                <div
                                    v-if="paidOrderData.point_discounts?.length"
                                    class="point-discount-detail"
                                >
                                    <a-tag
                                        v-for="pd in paidOrderData.point_discounts"
                                        :key="pd.id"
                                        class="point-discount-tag"
                                    >
                                        {{
                                            pd.point_type?.code ||
                                            pd.point_type?.name ||
                                            '點數'
                                        }}：-{{ formatAmount(pd.amount) }}
                                    </a-tag>
                                </div>
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單總額" :span="2">
                            <span class="paid-amount">
                                {{ formatAmount(paidOrderData.grand_total) }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="paidOrderData.placed_at"
                            label="下單時間"
                            :span="2"
                        >
                            {{ formatDateTime(paidOrderData.placed_at) }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>
            </div>
            <template #footer>
                <a-button @click="paidModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    :loading="paidLoading"
                    @click="handleConfirmPaid"
                >
                    確認標記已付款
                </a-button>
            </template>
        </a-modal>

        <!-- 取消訂單 Modal -->
        <a-modal
            v-model:open="cancelModalVisible"
            title="取消訂單"
            :width="600"
            :confirm-loading="cancelLoading"
            @ok="handleConfirmCancel"
            @cancel="cancelModalVisible = false"
        >
            <div v-if="cancelOrderData" class="cancel-modal-content">
                <!-- 訂單資訊 -->
                <div class="cancel-order-info">
                    <h3 class="cancel-section-title">訂單資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="訂單編號">
                            {{ cancelOrderData.order_no }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單狀態">
                            {{ getStatusText(cancelOrderData.status) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="商品小計">
                            {{ formatAmount(cancelOrderData.subtotal) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="cancelOrderData.shipping_fee > 0"
                            label="運費"
                        >
                            {{ formatAmount(cancelOrderData.shipping_fee) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="cancelOrderData.point_discount_total > 0"
                            label="積分抵扣"
                            :span="2"
                        >
                            <div class="point-discount-block">
                                <span class="point-discount">
                                    -{{
                                        formatAmount(
                                            cancelOrderData.point_discount_total
                                        )
                                    }}
                                </span>
                                <div
                                    v-if="
                                        cancelOrderData.point_discounts?.length
                                    "
                                    class="point-discount-detail"
                                >
                                    <a-tag
                                        v-for="pd in cancelOrderData.point_discounts"
                                        :key="pd.id"
                                        class="point-discount-tag"
                                    >
                                        {{
                                            pd.point_type?.code ||
                                            pd.point_type?.name ||
                                            '點數'
                                        }}：-{{ formatAmount(pd.amount) }}
                                    </a-tag>
                                </div>
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單總額" :span="2">
                            <span class="cancel-amount">
                                {{ formatAmount(cancelOrderData.grand_total) }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="cancelOrderData.placed_at"
                            label="下單時間"
                            :span="2"
                        >
                            {{ formatDateTime(cancelOrderData.placed_at) }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 操作提示 -->
                <div class="cancel-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作將自動回補庫存"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="cancelModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    danger
                    :loading="cancelLoading"
                    @click="handleConfirmCancel"
                >
                    確認取消訂單
                </a-button>
            </template>
        </a-modal>

        <!-- 標記已完成 Modal -->
        <a-modal
            v-model:open="completeModalVisible"
            title="標記已完成"
            :width="700"
            :confirm-loading="completeLoading"
            @ok="handleConfirmComplete"
            @cancel="completeModalVisible = false"
        >
            <div v-if="completeOrderData" class="complete-modal-content">
                <!-- 訂單資訊 -->
                <div class="complete-order-info">
                    <h3 class="complete-section-title">訂單資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="訂單編號">
                            {{ completeOrderData.order_no }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單狀態">
                            {{ getStatusText(completeOrderData.status) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="訂單金額" :span="2">
                            <span class="complete-amount">
                                {{
                                    formatAmount(completeOrderData.grand_total)
                                }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="completeOrderData.paid_at"
                            label="付款時間"
                            :span="2"
                        >
                            {{ formatDateTime(completeOrderData.paid_at) }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 購買商品 -->
                <div
                    v-if="
                        completeOrderData.items &&
                        completeOrderData.items.length > 0
                    "
                    class="complete-order-items"
                >
                    <h3 class="complete-section-title">購買商品</h3>
                    <a-table
                        :columns="itemColumns"
                        :data-source="completeOrderData.items"
                        :pagination="false"
                        size="small"
                        bordered
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'name'">
                                {{ record.name_snapshot }}
                            </template>
                            <template v-if="column.key === 'type'">
                                <a-tag>
                                    {{
                                        record.product?.type_display ||
                                        record.type_snapshot_display ||
                                        record.type_snapshot
                                    }}
                                </a-tag>
                            </template>
                            <template v-if="column.key === 'unit_price'">
                                {{ formatAmount(record.unit_price_snapshot) }}
                            </template>
                            <template v-if="column.key === 'subtotal'">
                                {{ formatAmount(record.subtotal) }}
                            </template>
                            <template v-if="column.key === 'shipping'">
                                <a-tag
                                    :class="
                                        record.requires_shipping
                                            ? 'shipping-yes'
                                            : 'shipping-no'
                                    "
                                >
                                    {{
                                        record.requires_shipping
                                            ? '需寄送'
                                            : '無需寄送'
                                    }}
                                </a-tag>
                            </template>
                        </template>
                    </a-table>
                </div>
            </div>
            <template #footer>
                <a-button @click="completeModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    :loading="completeLoading"
                    @click="handleConfirmComplete"
                >
                    確認標記已完成
                </a-button>
            </template>
        </a-modal>

        <!-- 退款 Modal -->
        <a-modal
            v-model:open="refundModalVisible"
            title="退款確認"
            :width="refundModalWidth"
            wrap-class-name="refund-confirm-modal"
            :confirm-loading="refundLoading"
            @ok="handleConfirmRefund"
            @cancel="refundModalVisible = false"
        >
            <div v-if="refundOrderData" class="refund-modal-content">
                <!-- 訂單資訊 -->
                <div class="refund-order-info">
                    <h3 class="refund-section-title">訂單</h3>
                    <a-descriptions :column="{ xs: 1, sm: 2 }" bordered size="small">
                        <a-descriptions-item label="訂單編號">
                            {{ refundOrderData.order_no }}
                        </a-descriptions-item>
                        <a-descriptions-item label="狀態">
                            {{ getStatusText(refundOrderData.status) }}
                        </a-descriptions-item>
                        <a-descriptions-item label="金額" :span="2">
                            <span class="refund-amount">
                                NT$ {{ formatAmount(refundOrderData.grand_total) }}
                            </span>
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 金流狀態 -->
                <div v-if="paymentStatus" class="refund-payment-status">
                    <h3 class="refund-section-title">金流</h3>
                    <a-descriptions :column="{ xs: 1, sm: 2 }" bordered size="small">
                        <a-descriptions-item label="支付狀態" :span="2">
                            <a-tag
                                :color="
                                    getPaymentStatusTagColor(
                                        paymentStatus.newebpay_status
                                    )
                                "
                            >
                                {{
                                    paymentStatus.newebpay_status_label ||
                                    paymentStatus.newebpay_status
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="paymentStatus.paid_at"
                            label="付款時間"
                        >
                            {{ formatDateTime(paymentStatus.paid_at) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="paymentStatus.trade_no"
                            label="交易編號"
                        >
                            <span class="refund-trade-no">{{ paymentStatus.trade_no }}</span>
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 退款設定 -->
                <div class="refund-settings">
                    <h3 class="refund-section-title">退款內容</h3>

                    <div
                        v-if="refundOrderData?.items && refundOrderData.items.length > 0"
                        class="refund-items"
                    >
                        <p class="refund-items__hint">
                            {{ refundItemsHint }}
                        </p>
                        <div
                            v-for="item in refundOrderData.items"
                            :key="item.id"
                            class="refund-item"
                        >
                            <div class="refund-item__row">
                                <div class="refund-item__main">
                                    <a-checkbox
                                        v-if="directRefundItems[item.id]"
                                        v-model:checked="directRefundItems[item.id].selected"
                                        :disabled="paymentStatus?.can_cancel_auth || Math.max(0, item.qty - (item.refunded_qty || 0)) <= 0"
                                    >
                                        <span class="refund-item__name">{{ item.name_snapshot }}</span>
                                        <span class="refund-item__price">NT$ {{ Number(item.unit_price_snapshot).toLocaleString() }}</span>
                                    </a-checkbox>
                                </div>
                                <div v-if="directRefundItems[item.id]" class="refund-item__qty">
                                    <template v-if="item.packages && item.packages.length > 0">
                                        <span>退 {{ directRefundItems[item.id].qty }}／可退 {{ Math.max(0, item.qty - (item.refunded_qty || 0)) }}</span>
                                    </template>
                                    <template v-else>
                                        <span>數量</span>
                                        <a-input-number
                                            v-model:value="directRefundItems[item.id].qty"
                                            :min="1"
                                            :max="Math.max(0, item.qty - (item.refunded_qty || 0))"
                                            :disabled="paymentStatus?.can_cancel_auth || !directRefundItems[item.id].selected"
                                            size="small"
                                            class="refund-item__qty-input"
                                        />
                                        <span class="refund-item__qty-max">／{{ Math.max(0, item.qty - (item.refunded_qty || 0)) }}</span>
                                    </template>
                                </div>
                            </div>
                            <div
                                v-if="directRefundItems[item.id]?.selected && item.packages && item.packages.length > 0"
                                class="refund-packages"
                            >
                                <p class="refund-packages__hint">選擇要作廢的套件</p>
                                <div
                                    v-for="(pkg, pIdx) in item.packages"
                                    :key="pIdx"
                                    class="refund-package"
                                >
                                    <a-checkbox
                                        v-model:checked="pkg.selected"
                                        :disabled="paymentStatus?.can_cancel_auth || !pkg.is_available"
                                        @change="syncAdminItemFromPackages(item.id)"
                                    >
                                        <span :class="pkg.is_available ? '' : 'refund-package--disabled'">
                                            套件 {{ pIdx + 1 }}：{{ pkg.items.map(i => `${i.name_snapshot} (#${i.id})`).join('、') }}
                                        </span>
                                    </a-checkbox>
                                    <a-tag :color="pkg.is_available ? 'green' : 'default'" size="small">
                                        {{ pkg.is_available ? '可退' : '不可退' }}
                                    </a-tag>
                                </div>
                            </div>
                        </div>
                    </div>

                    <a-form layout="vertical" class="refund-form">
                        <a-row :gutter="[12, 0]">
                            <a-col :xs="24" :sm="12">
                                <a-form-item label="手續費（NT$）">
                                    <a-input-number
                                        v-model:value="directRefundForm.fee_amount"
                                        :min="0"
                                        :max="computedDirectRefundBase"
                                        :disabled="paymentStatus?.can_cancel_auth"
                                        placeholder="尚未請款時不可設手續費"
                                        style="width: 100%"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :xs="24" :sm="12">
                                <a-form-item label="手續費說明">
                                    <a-input
                                        v-model:value="directRefundForm.fee_note"
                                        :disabled="paymentStatus?.can_cancel_auth"
                                        placeholder="選填，例如 10%"
                                    />
                                </a-form-item>
                            </a-col>
                        </a-row>
                        <a-form-item label="備註">
                            <a-textarea
                                v-model:value="directRefundForm.reason"
                                :rows="2"
                                placeholder="選填"
                            />
                        </a-form-item>
                    </a-form>
                    <div class="refund-summary-box">
                        <div class="refund-summary-box__row">
                            <span class="refund-label">{{ getActualRefundLabel(refundOrderData) }}</span>
                            <span class="refund-value">
                                NT$ {{ computedDirectActualRefund.toLocaleString() }}
                            </span>
                        </div>
                        <div v-if="directRefundForm.fee_amount > 0" class="refund-summary-box__sub">
                            實際刷卡 NT$ {{ Number(computedDirectRefundBase).toLocaleString() }} − 手續費 NT$ {{ Number(directRefundForm.fee_amount || 0).toLocaleString() }}
                            <template v-if="computedSelectedItemTotal > 0 && computedSelectedItemTotal !== computedDirectRefundBase">
                                （品項原價合計 NT$ {{ Number(computedSelectedItemTotal).toLocaleString() }}）
                            </template>
                        </div>
                    </div>
                </div>

                <!-- 操作提示 -->
                <div class="refund-action-hint">
                    <a-alert
                        :type="getRefundAlertType()"
                        :message="getRefundAlertMessage()"
                        show-icon
                    />
                </div>
            </div>
            <template #footer>
                <a-button @click="refundModalVisible = false">取消</a-button>
                <a-button
                    type="primary"
                    :loading="refundLoading"
                    :disabled="isRefundButtonDisabled()"
                    @click="handleConfirmRefund"
                >
                    {{ getRefundButtonText() }}
                </a-button>
            </template>
        </a-modal>

        <!-- 銀行匯款資料編輯 Modal -->
        <BankTransferEditModal
            v-model:open="bankTransferModalVisible"
            :order="selectedOrder"
            @success="loadOrders"
        />

        <!-- 發票資訊編輯 Modal -->
        <InvoiceEditModal
            v-model:open="invoiceModalVisible"
            :order="selectedOrder"
            @success="handleInvoiceEditSuccess"
        />

        <!-- 訂單狀態歷程 Modal -->
        <OrderStatusLogModal
            v-model:open="orderStatusLogVisible"
            :order-id="orderStatusLogTarget?.id"
            :order-no="orderStatusLogTarget?.order_no"
        />
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { message, Modal } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import {
    CopyOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DownOutlined
} from '@ant-design/icons-vue';
import { useRouter, useRoute } from 'vue-router';
import {
    getShopOrders,
    getShopOrder,
    markOrderPaid,
    markOrderCompleted,
    completeOrder,
    cancelOrder,
    refundOrder,
    getPaymentStatus,
    cancelAuth,
    updateBankTransferInfo,
    exportShopOrders
} from '@/api/products/orders';
import {
    getCancellationRequests,
    retryRefund
} from '@/api/products/orderCancellations';
import { searchUsers } from '@/api/users';
import { getProducts } from '@/api/products/index.js';
import DataTable from '@/components/common/DataTable.vue';
import DetailModal from '@/components/common/DetailModal.vue';
import OrderDetailContent from './components/OrderDetailContent.vue';
import BankTransferEditModal from './components/BankTransferEditModal.vue';
import InvoiceEditModal from './components/InvoiceEditModal.vue';
import OrderStatusLogModal from '@/components/orders/OrderStatusLogModal.vue';
import { canShowOrderStatusLog } from '@/utils/orderStatusHistory';
import formatDateTime from '@/utils/formatDateTime';
import { getOrderCheckoutInvoice } from '@/utils/orderInvoiceDisplay';
import {
    getPaymentMethodText as resolvePaymentMethodText,
    getPaymentMethodTagColor as resolvePaymentMethodTagColor,
    getPaymentGatewayText as resolvePaymentGatewayText,
    getPaymentGatewayTagColor as resolvePaymentGatewayTagColor,
    getPaymentAccountText as resolvePaymentAccountText,
    PAYMENT_ACCOUNT_LABELS
} from '@/utils/paymentUtils';
import {
    loadVisibleColumns,
    saveVisibleColumns
} from '@/utils/columnSettingsStorage';
import dayjs from 'dayjs';
import { useAdminInfo } from '@/stores/useAdminInfo';
import {
    getOrderDeskProfileFromRouteMeta,
    resolveOrderDeskActionAllowed
} from '@/views/products/orderDeskProfiles.js';

const router = useRouter();
const route = useRoute();
const adminStore = useAdminInfo();

const orderDeskProfile = computed(() =>
    getOrderDeskProfileFromRouteMeta(route.meta)
);

// 權限檢查 × 訂單工作面 profile
const canViewOrder = computed(() => adminStore.hasPermissionCode('150301'));
const canModifyStatus = computed(() =>
    resolveOrderDeskActionAllowed(
        orderDeskProfile.value,
        'status',
        adminStore.hasPermissionCode('150302')
    )
);
const canShipOrder = computed(() =>
    resolveOrderDeskActionAllowed(
        orderDeskProfile.value,
        'ship',
        adminStore.hasPermissionCode('150303')
    )
);
const canCompleteOrder = computed(() =>
    resolveOrderDeskActionAllowed(
        orderDeskProfile.value,
        'complete',
        adminStore.hasPermissionCode('150304')
    )
);
const canRefundOrder = computed(() =>
    resolveOrderDeskActionAllowed(
        orderDeskProfile.value,
        'refund',
        adminStore.hasPermissionCode('150305')
    )
);
const canCancelOrder = computed(() =>
    resolveOrderDeskActionAllowed(
        orderDeskProfile.value,
        'cancel',
        adminStore.hasPermissionCode('150306')
    )
);
const canViewMember = computed(() => adminStore.hasPermissionCode('100110'));
const canExportOrders = computed(() =>
    resolveOrderDeskActionAllowed(
        orderDeskProfile.value,
        'financeExport',
        adminStore.hasPermissionCode('150301')
    )
);
const exportLoading = ref(false);

// 資料狀態
const orders = ref([]);
const loading = ref(false);

// 篩選器狀態
const filters = reactive({
    status: undefined,
    user_id: undefined,
    product_id: undefined,
    order_no: '',
    date_from: undefined,
    date_to: undefined,
    min_amount: undefined,
    max_amount: undefined,
    payment_account_key: undefined
});

// 表格標頭排序狀態（由表格 @change 的 sorter 更新，用於 API sort_by / sort_order）
const sortState = reactive({
    columnKey: null,
    order: null // 'ascend' | 'descend' | null
});

// 日期範圍
const dateRange = ref(null);

// 用戶搜尋
const userOptions = ref([]);
const userLoading = ref(false);
let userSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

// 商品搜尋
const productOptions = ref([]);
const productLoading = ref(false);
let productSearchTimer = null;

// 分頁設定
const pagination = reactive({
    current: 1,
    pageSize: 15,
    total: 0
});

// Modal 狀態
const detailModalVisible = ref(false);
const selectedOrder = ref(null);
const activeTab = ref('order');
const cancellationRequest = ref(null);
const bankTransferModalVisible = ref(false);
const invoiceModalVisible = ref(false);
const orderStatusLogVisible = ref(false);
const orderStatusLogTarget = ref(null);
// 退款 Modal 狀態
const refundModalVisible = ref(false);
const refundOrderData = ref(null);
const paymentStatus = ref(null);
const refundLoading = ref(false);
/** 隨螢幕寬度伸縮，大螢幕上限 760px */
const refundModalWidth = 'min(760px, calc(100vw - 32px))';

const directRefundForm = reactive({
    fee_amount: 0,
    fee_note: '',
    reason: ''
});

const directRefundItems = reactive({});

const computedSelectedItemTotal = computed(() => {
    if (!refundOrderData.value?.items) return 0;
    return refundOrderData.value.items.reduce((sum, item) => {
        const state = directRefundItems[item.id];
        if (state?.selected && state.qty > 0) {
            return sum + (Number(item.unit_price_snapshot) * Number(state.qty));
        }
        return sum;
    }, 0);
});

/** 訂單品項快照合計（商品原價 × 數量；分期時可能小於實際刷卡額） */
const computedOrderItemsSnapshotTotal = computed(() => {
    if (!refundOrderData.value?.items) return 0;
    return refundOrderData.value.items.reduce((sum, item) => {
        return sum + (Number(item.unit_price_snapshot) * Number(item.qty || 0));
    }, 0);
});

/**
 * 直接退款計算基底：以實際刷卡金額（grand_total）為準。
 * 分期時品項 unit_price_snapshot 多半是商品原價，會小於 grand_total；
 * 有勾選時依「勾選快照 / 整單快照」比例攤到 grand_total。
 */
const computedDirectRefundBase = computed(() => {
    if (!refundOrderData.value) return 0;
    const charged = Number(refundOrderData.value.grand_total || 0);
    const selectedSnapshot = computedSelectedItemTotal.value;
    if (selectedSnapshot <= 0) return charged;

    const itemsSnapshot = computedOrderItemsSnapshotTotal.value;
    if (itemsSnapshot <= 0) return charged;
    // 勾選等同整單品項 → 直接用實際刷卡額，避免比例四捨五入
    if (selectedSnapshot >= itemsSnapshot) return charged;
    return Math.round((selectedSnapshot / itemsSnapshot) * charged);
});

const computedDirectActualRefund = computed(() => {
    const fee = Number(directRefundForm.fee_amount || 0);
    return Math.max(0, computedDirectRefundBase.value - fee);
});

// 標記已完成 Modal 狀態
const completeModalVisible = ref(false);
const completeOrderData = ref(null);
const completeLoading = ref(false);

// 標記已付款 Modal 狀態
const paidModalVisible = ref(false);
const paidOrderData = ref(null);
const paidLoading = ref(false);

// 取消訂單 Modal 狀態
const cancelModalVisible = ref(false);
const cancelOrderData = ref(null);
const cancelLoading = ref(false);

// 訂單狀態選項
const QUICK_STATUS_OPTIONS = [
    { label: '全部', value: undefined },
    { label: '待付款', value: 'pending' },
    { label: '已付款', value: 'paid' },
    { label: '已完成', value: 'completed' }
];

const statusOptions = [
    { label: '全部', value: undefined },
    { label: '待付款', value: 'pending' },
    { label: '已付款', value: 'paid' },
    { label: '已完成', value: 'completed' },
    { label: '已取消', value: 'cancelled' },
    { label: '取消審核中', value: 'cancellation_pending' },
    { label: '退款中', value: 'refunding' },
    { label: '部分退款', value: 'partially_refunded' },
    { label: '已退款', value: 'refunded' },
    { label: '退款失敗', value: 'refund_failed' }
];

const paymentAccountOptions = Object.entries(PAYMENT_ACCOUNT_LABELS).map(
    ([value, label]) => ({ label, value })
);

// 欄位顯示設定
const ORDER_COLUMN_SETTINGS_KEY = 'orders_list_visible_columns';
const ORDER_COLUMN_SETTINGS_VERSION = 1;
const ORDER_COLUMN_LEGACY_KEYS = [
    'orders_list_visible_columns_v5',
    'orders_list_visible_columns_v4',
    'orders_list_visible_columns_v3',
    'orders_list_visible_columns_v2'
];

// 表格欄位定義 - 寬度依內容密度配置（操作欄不搶主資料空間）
const allColumns = [
    {
        title: '訂單編號',
        key: 'order_no',
        dataIndex: 'order_no',
        width: 156,
        fixed: 'left',
        sorter: true
    },
    {
        title: '付款單號',
        key: 'payment_no',
        width: 156
    },
    {
        title: '會員',
        key: 'user',
        dataIndex: 'user',
        width: 112,
        sorter: true
    },
    {
        title: '推薦人',
        key: 'referrer',
        width: 88
    },
    {
        title: '付款方式',
        key: 'payment',
        dataIndex: 'payment',
        width: 148,
        align: 'center',
        sorter: true
    },
    {
        title: '狀態',
        key: 'status',
        dataIndex: 'status',
        width: 92,
        align: 'center',
        sorter: true
    },
    {
        title: '匯款銀行',
        key: 'transfer_bank',
        width: 120
    },
    {
        title: '匯款戶名',
        key: 'account_name',
        width: 100
    },
    {
        title: '匯款帳號後五碼',
        key: 'account_last_5',
        width: 120
    },
    {
        title: '金額',
        key: 'amount',
        dataIndex: 'grand_total',
        width: 108,
        align: 'right',
        sorter: true
    },
    {
        title: '商品',
        key: 'items',
        width: 220,
        ellipsis: true
    },
    {
        title: '結帳發票',
        key: 'user_invoice',
        width: 156,
        ellipsis: true
    },
    {
        title: '時間',
        key: 'order_times',
        width: 138,
        sorter: true
    },
    {
        title: '操作',
        key: 'actions',
        width: 128,
        fixed: 'right',
        align: 'center'
    }
];

// 預設可見欄位（付款單號／轉帳欄預設隱藏，列表較不擠；可在欄位設置中開啟）
const DEFAULT_VISIBLE_COLUMNS = allColumns
    .map((col) => col.key)
    .filter(
        (key) =>
            ![
                'payment_no',
                'transfer_bank',
                'account_name',
                'account_last_5',
                'referrer'
            ].includes(key)
    );

const visibleColumns = ref([...DEFAULT_VISIBLE_COLUMNS]);

const columnSettingsModalVisible = ref(false);

// 篩選區摺疊狀態，預設收合（[] = 收合，['filters'] = 展開）
const filterCollapseActiveKey = ref([]);

// 標頭排序欄位 key 對應 API sort_by
const SORT_FIELD_MAP = {
    amount: 'grand_total',
    order_times: 'placed_at',
    order_no: 'order_no',
    user: 'user_name',
    payment: 'payment_method',
    payment_account: 'payment_account_key',
    status: 'status'
};

const ITEM_TYPE_LABELS = {
    course: '課程',
    physical: '實體',
    virtual: '虛寶',
    ticket: '票券'
};

const getItemTypeLabel = (type) => ITEM_TYPE_LABELS[type] || '商品';

const getItemTypeClass = (type) => {
    if (type === 'course') return 'item-type-tag--course';
    if (type === 'physical') return 'item-type-tag--physical';
    if (type === 'virtual') return 'item-type-tag--virtual';
    if (type === 'ticket') return 'item-type-tag--ticket';
    return 'item-type-tag--default';
};

const columns = computed(() => {
    const base = allColumns.filter((col) =>
        visibleColumns.value.includes(col.key)
    );
    return base.map((col) => {
        if (col.sorter) {
            return {
                ...col,
                sortOrder:
                    sortState.columnKey === col.key
                        ? sortState.order
                        : undefined
            };
        }
        return col;
    });
});

// 商品表格欄位
const itemColumns = [
    {
        title: '商品名稱',
        key: 'name',
        width: 200
    },
    {
        title: '類型',
        key: 'type',
        width: 100,
        align: 'center'
    },
    {
        title: '單價',
        key: 'unit_price',
        width: 120,
        align: 'right'
    },
    {
        title: '數量',
        dataIndex: 'qty',
        width: 80,
        align: 'center'
    },
    {
        title: '小計',
        key: 'subtotal',
        width: 120,
        align: 'right'
    },
    {
        title: '寄送',
        key: 'shipping',
        width: 100,
        align: 'center'
    }
];

// 匯款資訊是否有填任一欄（給列表的 icon 用）
const hasBankTransferInfo = (order) => {
    if (!order) return false;
    return !!(
        order.transfer_date ||
        order.transfer_bank ||
        order.account_name ||
        order.account_last_5
    );
};

// 載入訂單列表
const loadOrders = async () => {
    try {
        loading.value = true;

        const params = {
            page: pagination.current,
            per_page: pagination.pageSize
        };

        // 添加篩選條件
        if (filters.status) params.status = filters.status;
        if (filters.user_id) params.user_id = filters.user_id;
        if (filters.product_id) params.product_id = filters.product_id;
        if (filters.order_no) params.order_no = filters.order_no;
        if (filters.date_from) params.date_from = filters.date_from;
        if (filters.date_to) params.date_to = filters.date_to;
        if (filters.min_amount) params.min_amount = filters.min_amount;
        if (filters.max_amount) params.max_amount = filters.max_amount;
        if (filters.payment_account_key) {
            params.payment_account_key = filters.payment_account_key;
        }

        if (sortState.columnKey && sortState.order) {
            const sortBy = SORT_FIELD_MAP[sortState.columnKey];
            if (sortBy) {
                params.sort_by = sortBy;
                params.sort_order =
                    sortState.order === 'ascend' ? 'asc' : 'desc';
            }
        }
        // 未點擊標頭排序時不帶 sort 參數，後端依建立時間由新到舊

        const response = await getShopOrders(params);

        if (response && response.result) {
            orders.value = response.data?.data || [];
            pagination.total = response.data?.pagination?.total || 0;
            pagination.current = response.data?.pagination?.current_page || 1;
        } else {
            console.warn('訂單列表回應異常:', response);
            orders.value = [];
            pagination.total = 0;
        }
    } catch (error) {
        console.error('載入訂單列表失敗:', error);
        showErrorMessage('載入訂單列表失敗: ' + (error.message || '未知錯誤'));
        orders.value = [];
        pagination.total = 0;
    } finally {
        loading.value = false;
    }
};

// 載入保存的欄位設置
const loadColumnSettings = () => {
    try {
        visibleColumns.value = loadVisibleColumns({
            storageKey: ORDER_COLUMN_SETTINGS_KEY,
            schemaVersion: ORDER_COLUMN_SETTINGS_VERSION,
            allColumnKeys: allColumns.map((col) => col.key),
            defaultVisible: DEFAULT_VISIBLE_COLUMNS,
            legacyKeys: ORDER_COLUMN_LEGACY_KEYS
        });
    } catch (error) {
        console.error('載入訂單欄位設置失敗:', error);
    }
};

// 保存欄位設置到 localStorage
const saveColumnSettings = () => {
    try {
        saveVisibleColumns(
            ORDER_COLUMN_SETTINGS_KEY,
            ORDER_COLUMN_SETTINGS_VERSION,
            visibleColumns.value
        );
        columnSettingsModalVisible.value = false;
        message.success('欄位設置已保存');
    } catch (error) {
        console.error('保存訂單欄位設置失敗:', error);
        showErrorMessage('保存欄位設置失敗');
    }
};

// 顯示欄位設置 Modal
const showColumnSettingsModal = () => {
    columnSettingsModalVisible.value = true;
};

// 用戶搜尋
const fetchUserOptions = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
        userOptions.value = [];
        return;
    }
    try {
        userLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];
            userOptions.value = arr.map((u) => ({
                label: `${u.name || u.user_name || `#${u.id}`}`,
                value: u.id
            }));
        }
    } catch (e) {
        // 靜默失敗即可
    } finally {
        userLoading.value = false;
    }
};

const onUserSearch = (val) => {
    clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(
        () => fetchUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

const handleUserSelect = () => {
    pagination.current = 1;
    loadOrders();
};

// 日期範圍變更
const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
        filters.date_from = dayjs(dates[0]).format('YYYY-MM-DD');
        filters.date_to = dayjs(dates[1]).format('YYYY-MM-DD');
    } else {
        filters.date_from = undefined;
        filters.date_to = undefined;
        dateRange.value = null;
    }
};

// 查詢
const handleSearch = () => {
    pagination.current = 1;
    loadOrders();
};

const handleExport = async () => {
    if (!canExportOrders.value) {
        message.warning('此工作面無法匯出');
        return;
    }
    exportLoading.value = true;
    try {
        const params = {};
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params[key] = value;
            }
        });
        if (sortState.columnKey && sortState.order) {
            const sortBy = SORT_FIELD_MAP[sortState.columnKey];
            if (sortBy) {
                params.sort_by = sortBy;
                params.sort_order =
                    sortState.order === 'ascend' ? 'asc' : 'desc';
            }
        }
        const blob = await exportShopOrders(params);
        if (blob?.type?.includes('application/json')) {
            const text = await blob.text();
            const json = JSON.parse(text);
            message.error(json.message || '匯出失敗');
            return;
        }
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
            'download',
            `訂單列表_${formatDateTime(new Date(), 'YYYYMMDD_HHmmss')}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch {
        message.error('匯出失敗');
    } finally {
        exportLoading.value = false;
    }
};

const applyQuickStatus = (status) => {
    filters.status = status;
    pagination.current = 1;
    loadOrders();
};

// 複製付款單號至剪貼簿
const copyPaymentNo = async (text) => {
    if (!text) return;
    try {
        await navigator.clipboard.writeText(text);
        message.success('已複製付款單號');
    } catch (err) {
        console.error('複製失敗:', err);
        message.error('複製失敗');
    }
};

const onProductSearch = (value) => {
    clearTimeout(productSearchTimer);
    productSearchTimer = setTimeout(async () => {
        const query = value?.trim();
        if (!query || query.length < 1) {
            productOptions.value = [];
            return;
        }
        productLoading.value = true;
        try {
            const res = await getProducts({
                keyword: query,
                per_page: 30,
                page: 1
            });
            const list = Array.isArray(res?.data?.data)
                ? res.data.data
                : Array.isArray(res?.data)
                  ? res.data
                  : [];
            productOptions.value = list.map((p) => ({
                value: p.id,
                label: p.name || `#${p.id}`
            }));
        } catch {
            productOptions.value = [];
        } finally {
            productLoading.value = false;
        }
    }, USER_SEARCH_DEBOUNCE_MS);
};

// 重置
const handleReset = () => {
    filters.status = undefined;
    filters.user_id = undefined;
    filters.product_id = undefined;
    filters.order_no = '';
    filters.date_from = undefined;
    filters.date_to = undefined;
    filters.min_amount = undefined;
    filters.max_amount = undefined;
    filters.payment_account_key = undefined;
    sortState.columnKey = null;
    sortState.order = null;
    dateRange.value = null;
    userOptions.value = [];
    productOptions.value = [];
    pagination.current = 1;
    loadOrders();
};

// 表格變化處理
const handleTableChange = ({ pagination: pag, sorter: sorterPayload }) => {
    if (pag) {
        pagination.current = pag.current;
        pagination.pageSize = pag.pageSize;
    }
    if (
        sorterPayload &&
        (sorterPayload.columnKey !== undefined ||
            sorterPayload.order !== undefined)
    ) {
        sortState.columnKey = sorterPayload.columnKey ?? null;
        sortState.order = sorterPayload.order ?? null;
    }
    loadOrders();
};

// 狀態相關函數
const getActualRefundLabel = (record) => {
    if (!record) return '實際退款';
    const gateway = record.payment_gateway || record.payment_method || record.payment?.gateway || record.gateway || '';
    if (gateway === 'point_exchange') {
        return '實際退還';
    }
    return '實際退款';
};

const getStatusText = (status) => {
    const statusMap = {
        pending: '待付款',
        paid: '已付款',
        completed: '已完成',
        cancelled: '已取消',
        refunding: '退款中',
        partially_refunded: '部分退款',
        refunded: '已退款',
        refund_failed: '退款失敗',
        cancellation_pending: '取消審核中'
    };
    return statusMap[status] || status || '-';
};

const getStatusTagClass = (status) => {
    const classMap = {
        pending: 'pending',
        paid: 'paid',
        completed: 'completed',
        cancelled: 'cancelled',
        refunding: 'refunding',
        partially_refunded: 'partially-refunded',
        refunded: 'refunded',
        refund_failed: 'refund-failed',
        cancellation_pending: 'cancellation-pending'
    };
    return classMap[status] || '';
};

// 金額格式化
const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '0';
    return Number(amount).toLocaleString('zh-TW');
};

// 計算實際退款金額 (相容直接退款、退款紀錄與全額退款)
const getActualRefundAmount = (record) => {
    if (!record) return 0;
    const isPurePoint = (record.grand_total === 0 || record.payment_method === 'point' || record.payment_gateway === 'point_exchange' || record.paymentGateway === 'point_exchange');
    if (isPurePoint) return 0;
    const candidates = [
        record.actual_refund_amount,
        record.cancellation_request?.actual_refund_amount,
        record.cancellationRequest?.actual_refund_amount,
        record.cancellation_requests?.[0]?.actual_refund_amount,
        record.cancellation_request?.requested_amount,
        record.cancellationRequest?.requested_amount,
        record.cancellation_requests?.[0]?.requested_amount
    ];
    for (const val of candidates) {
        if (val !== undefined && val !== null && Number(val) > 0) {
            return Number(val);
        }
    }
    if (record.items && record.items.length > 0) {
        const itemSum = record.items.reduce((sum, item) => {
            const itemAmount = Number(item.refunded_amount || 0);
            if (itemAmount > 0) return sum + itemAmount;
            const refQty = Number(item.refunded_qty || 0);
            if (refQty > 0) return sum + (Number(item.unit_price_snapshot || item.unit_price || 0) * refQty);
            return sum;
        }, 0);
        if (itemSum > 0) return itemSum;
    }
    if (record.status === 'refunded') {
        return Number(record.grand_total || record.subtotal || 0);
    }
    return 0;
};

// 寄送方式文字
const getShippingMethodText = (method) => {
    const methodMap = {
        home_delivery: '宅配',
        convenience_store: '超商取貨',
        post_office: '郵局'
    };
    return methodMap[method] || method || '-';
};

// 付款方式文字（支援 credit、bank、atm、linepay、cvs、barcode、藍新金流與銀行匯款）
const getPaymentMethodText = (method) => {
    return resolvePaymentMethodText(method);
};

// 付款方式 Tag 顏色
const getPaymentMethodTagColor = (method) => {
    return resolvePaymentMethodTagColor(method);
};

// 付款閘道文字（藍新金流、銀行匯款、純積分兌換）
const getPaymentGatewayText = (gateway) => {
    return resolvePaymentGatewayText(gateway);
};

// 付款閘道 Tag 顏色
const getPaymentGatewayTagColor = (gateway) => {
    return resolvePaymentGatewayTagColor(gateway);
};

// 從訂單或 payments 取得用於顯示的付款方式與閘道（訂單層級可能為 null，需 fallback payments[0]）
const getOrderPaymentDisplay = (order) => {
    if (!order) return { method: null, gateway: null };
    const method = order.payment_method ?? order.payments?.[0]?.method ?? null;
    const gateway =
        order.payment_gateway ?? order.payments?.[0]?.gateway ?? null;
    return { method, gateway };
};

const getPaymentAccountText = (order) => {
    return resolvePaymentAccountText(order);
};

// 開啟銀行匯款資料編輯 Modal
const openBankTransferEdit = () => {
    if (!selectedOrder.value) return;
    bankTransferModalVisible.value = true;
};

// 開啟發票資訊編輯 Modal
const openInvoiceEdit = () => {
    if (!selectedOrder.value) return;
    invoiceModalVisible.value = true;
};

// 發票資訊編輯成功後重新載入
const handleInvoiceEditSuccess = async () => {
    await loadOrders();
    if (selectedOrder.value?.id) {
        const response = await getShopOrder(selectedOrder.value.id);
        if (response && response.result) {
            selectedOrder.value = response.data;
        }
    }
};

// 查看會員詳情
const handleViewMember = (userId) => {
    if (!userId) return;
    if (!canViewMember.value) {
        message.warning('您沒有查看會員詳情的權限');
        return;
    }
    router.push(`/members/${userId}`);
};

const hasOrderMenuItems = (record) => {
    if (!record) return false;
    if (record.status === 'pending') {
        return canModifyStatus.value || canCancelOrder.value;
    }
    if (record.status === 'paid') {
        return canCompleteOrder.value || canRefundOrder.value;
    }
    if (record.status === 'refund_failed') {
        return canRefundOrder.value;
    }
    return canShowOrderStatusLog(record);
};

const handleOrderMenuClick = ({ key }, record) => {
    if (key === 'mark-paid') {
        handleMarkPaid(record);
        return;
    }
    if (key === 'cancel') {
        handleCancelOrder(record);
        return;
    }
    if (key === 'complete') {
        handleMarkCompleted(record);
        return;
    }
    if (key === 'refund') {
        handleRefundOrder(record);
        return;
    }
    if (key === 'logs') {
        openOrderLogs(record);
    }
};

// 查看訂單狀態歷程
const openOrderLogs = (record) => {
    if (!canShowOrderStatusLog(record)) return;
    orderStatusLogTarget.value = record;
    orderStatusLogVisible.value = true;
};

// 查看訂單詳情
const handleViewDetail = async (record) => {
    if (!canViewOrder.value) {
        message.warning('您沒有查看訂單詳情的權限');
        return;
    }
    try {
        // 每次都重新載入完整資料，以取得 payment 等完整資訊
        loading.value = true;
        const response = await getShopOrder(record.id);
        if (response && response.result) {
            selectedOrder.value = response.data;

            activeTab.value = 'order';
            detailModalVisible.value = true;

            // 只有當訂單狀態為取消審核中或已取消時，才查詢取消申請
            const orderStatus = response.data.status;
            if (
                orderStatus === 'cancellation_pending' ||
                orderStatus === 'cancelled'
            ) {
                await loadCancellationRequest(record.order_no);
            } else {
                // 不符合條件時，清空取消申請資料
                cancellationRequest.value = null;
            }
        } else {
            showErrorMessage('載入訂單詳情失敗');
        }
    } catch (error) {
        console.error('載入訂單詳情失敗:', error);
        showErrorMessage('載入訂單詳情失敗: ' + (error.message || '未知錯誤'));
    } finally {
        loading.value = false;
    }
};

// 載入取消申請
const loadCancellationRequest = async (orderNo) => {
    try {
        const response = await getCancellationRequests({
            order_no: orderNo,
            per_page: 1,
            page: 1
        });
        if (response && response.result) {
            const requests = response.data?.data || [];
            // 取得最新的已核准申請（如果有多個）
            cancellationRequest.value =
                requests.find((req) => req.status === 'approved') || null;
        } else {
            cancellationRequest.value = null;
        }
    } catch (error) {
        console.error('載入取消申請失敗:', error);
        cancellationRequest.value = null;
    }
};

// 檢查是否可以重試退款
const canRetryRefund = computed(() => {
    if (!cancellationRequest.value) return false;
    const req = cancellationRequest.value;
    // 必須符合：已核准 + 需要退款 + 退款狀態為 failed/processing/pending（非 completed）
    return (
        req.status === 'approved' &&
        req.refund_required === true &&
        ['failed', 'processing', 'pending'].includes(req.refund_status)
    );
});

// 取得退款狀態文字
const getRefundStatusText = (status) => {
    const statusMap = {
        none: '無需退款',
        pending: '待退款',
        processing: '退款處理中',
        completed: '退款已完成',
        failed: '退款失敗'
    };
    return statusMap[status] || status || '-';
};

// 取得退款狀態顏色
const getRefundStatusColor = (status) => {
    const colorMap = {
        none: 'default',
        pending: 'orange',
        processing: 'blue',
        completed: 'green',
        failed: 'red'
    };
    return colorMap[status] || 'default';
};

// 重試退款
const handleRetryRefund = () => {
    if (!cancellationRequest.value) return;

    Modal.confirm({
        title: '確認重試退款',
        content: `確定要為取消申請 #${cancellationRequest.value.id} 重試退款嗎？`,
        okText: '確認重試',
        cancelText: '取消',
        okType: 'primary',
        onOk: async () => {
            try {
                loading.value = true;
                const response = await retryRefund(
                    cancellationRequest.value.id
                );
                if (response && response.result) {
                    message.success('退款重試中，請稍候查看結果');
                    // 重新載入取消申請資訊
                    await loadCancellationRequest(selectedOrder.value.order_no);
                    // 重新載入訂單詳情
                    const orderResponse = await getShopOrder(
                        selectedOrder.value.id
                    );
                    if (orderResponse && orderResponse.result) {
                        selectedOrder.value = orderResponse.data;
                    }
                } else {
                    showErrorMessage(response?.message || '重試退款失敗');
                }
            } catch (error) {
                console.error('重試退款失敗:', error);
                showErrorMessage(
                    '重試退款失敗: ' + (error.message || '未知錯誤')
                );
            } finally {
                loading.value = false;
            }
        }
    });
};

// Tab 切換處理
const handleTabChange = (key) => {
    activeTab.value = key;
};

// 交易狀態文字
const getTransactionStatusText = (status) => {
    const statusUpper = status?.toUpperCase();
    const statusMap = {
        SUCCESS: '成功',
        FAILED: '失敗',
        PENDING: '處理中',
        INITIATED: '已建立',
        CANCELLED: '已取消',
        REFUNDED: '已退款'
    };
    return statusMap[statusUpper] || status || '-';
};

// 交易狀態顏色
const getTransactionStatusColor = (status) => {
    const colorMap = {
        SUCCESS: 'green',
        FAILED: 'red',
        PENDING: 'orange',
        INITIATED: 'blue',
        CANCELLED: 'default',
        REFUNDED: 'purple'
    };
    return colorMap[status] || 'default';
};

// 格式化 JSON 字串為可讀格式
const formatJsonString = (jsonString) => {
    if (!jsonString) return '';
    try {
        // 如果是字串，先解析
        const parsed =
            typeof jsonString === 'string'
                ? JSON.parse(jsonString)
                : jsonString;
        // 格式化為可讀的 JSON
        return JSON.stringify(parsed, null, 2);
    } catch (error) {
        // 如果解析失敗，返回原始字串
        console.error('JSON 解析失敗:', error);
        return jsonString;
    }
};

// 複製 JSON 到剪貼簿
const copyJsonToClipboard = async (jsonString) => {
    try {
        const formattedJson = formatJsonString(jsonString);
        await navigator.clipboard.writeText(formattedJson);
        message.success('已複製到剪貼簿');
    } catch (error) {
        console.error('複製失敗:', error);
        // 降級方案：使用傳統方法
        const textArea = document.createElement('textarea');
        textArea.value = formatJsonString(jsonString);
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
            document.execCommand('copy');
            message.success('已複製到剪貼簿');
        } catch (err) {
            showErrorMessage('複製失敗，請手動複製');
        }
        document.body.removeChild(textArea);
    }
};

// 支付狀態文字
const getPaymentStatusText = (status) => {
    const statusLower = status?.toLowerCase();
    const statusMap = {
        pending: '待處理',
        processing: '處理中',
        captured: '已扣款',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消',
        partially_refunded: '部分退款',
        refunded: '已退款',
        refunding: '退款中',
        initiated: '已建立'
    };
    return statusMap[statusLower] || status || '-';
};

// 支付狀態顏色
const getPaymentStatusColor = (status) => {
    const colorMap = {
        pending: 'orange',
        processing: 'blue',
        captured: 'green',
        completed: 'green',
        failed: 'red',
        cancelled: 'default',
        partially_refunded: 'orange',
        refunded: 'purple',
        refunding: 'orange'
    };
    return colorMap[status] || 'default';
};

// 金流狀態標籤顏色（用於退款 Modal）
const getPaymentStatusTagColor = (status) => {
    const colorMap = {
        unpaid: 'orange',
        authorized: 'blue',
        closed: 'green',
        refunded: 'purple',
        cancelled: 'default'
    };
    return colorMap[status] || 'default';
};

// 訂單基本資訊（用於 DetailModal）
const orderBasicInfo = computed(() => {
    if (!selectedOrder.value) return [];
    const order = selectedOrder.value;
    const info = [
        { label: '訂單編號', value: order.order_no },
        { label: '訂單狀態', value: getStatusText(order.status) },
        { label: '商品小計', value: formatAmount(order.subtotal) }
    ];

    // 如果有運費，顯示運費
    if (order.shipping_fee > 0) {
        info.push({ label: '運費', value: formatAmount(order.shipping_fee) });
    }

    // 如果有積分抵扣，顯示積分抵扣
    if (order.point_discount_total > 0) {
        info.push({
            label: '積分抵扣',
            value: `-${formatAmount(order.point_discount_total)}`
        });
    }

    // 最後顯示訂單總額
    info.push({
        label: '訂單總額',
        value: formatAmount(order.grand_total)
    });

    return info;
});

// 標記已付款
const handleMarkPaid = async (record) => {
    if (!canModifyStatus.value) {
        message.warning('您沒有修改訂單狀態的權限');
        return;
    }

    // 載入訂單完整資料
    try {
        paidLoading.value = true;
        const orderResponse = await getShopOrder(record.id);
        if (orderResponse && orderResponse.result) {
            paidOrderData.value = orderResponse.data;
            paidModalVisible.value = true;
        } else {
            showErrorMessage('載入訂單資料失敗');
        }
    } catch (error) {
        console.error('載入訂單資料失敗:', error);
        showErrorMessage('載入訂單資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        paidLoading.value = false;
    }
};

// 確認標記已付款
const handleConfirmPaid = async () => {
    if (!paidOrderData.value) return;

    try {
        paidLoading.value = true;
        const response = await markOrderPaid(paidOrderData.value.id);
        if (response && response.result) {
            message.success('訂單已標記為已付款');
            paidModalVisible.value = false;
            loadOrders();
        } else {
            showErrorMessage(response?.message || '操作失敗');
        }
    } catch (error) {
        console.error('標記已付款失敗:', error);
        showErrorMessage('操作失敗: ' + (error.message || '未知錯誤'));
    } finally {
        paidLoading.value = false;
    }
};

// 標記已完成
const handleMarkCompleted = async (record) => {
    if (!canCompleteOrder.value) {
        message.warning('您沒有完成訂單的權限');
        return;
    }

    // 載入訂單完整資料
    try {
        completeLoading.value = true;
        const orderResponse = await getShopOrder(record.id);
        if (orderResponse && orderResponse.result) {
            completeOrderData.value = orderResponse.data;
            completeModalVisible.value = true;
        } else {
            showErrorMessage('載入訂單資料失敗');
        }
    } catch (error) {
        console.error('載入訂單資料失敗:', error);
        showErrorMessage('載入訂單資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        completeLoading.value = false;
    }
};

// 確認標記已完成
const handleConfirmComplete = async () => {
    if (!completeOrderData.value) return;

    try {
        completeLoading.value = true;
        const response = await completeOrder(completeOrderData.value.id);
        if (response && response.result) {
            message.success('訂單已標記為已完成');
            completeModalVisible.value = false;
            loadOrders();
        } else {
            showErrorMessage(response?.message || '操作失敗');
        }
    } catch (error) {
        console.error('標記已完成失敗:', error);
        showErrorMessage('操作失敗: ' + (error.message || '未知錯誤'));
    } finally {
        completeLoading.value = false;
    }
};

// 取消訂單
const handleCancelOrder = async (record) => {
    if (!canCancelOrder.value) {
        message.warning('您沒有取消訂單的權限');
        return;
    }

    // 載入訂單完整資料
    try {
        cancelLoading.value = true;
        const orderResponse = await getShopOrder(record.id);
        if (orderResponse && orderResponse.result) {
            cancelOrderData.value = orderResponse.data;
            cancelModalVisible.value = true;
        } else {
            showErrorMessage('載入訂單資料失敗');
        }
    } catch (error) {
        console.error('載入訂單資料失敗:', error);
        showErrorMessage('載入訂單資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        cancelLoading.value = false;
    }
};

// 確認取消訂單
const handleConfirmCancel = async () => {
    if (!cancelOrderData.value) return;

    try {
        cancelLoading.value = true;
        const response = await cancelOrder(cancelOrderData.value.id);
        if (response && response.result) {
            message.success('訂單已取消');
            cancelModalVisible.value = false;
            loadOrders();
        } else {
            showErrorMessage(response?.message || '取消訂單失敗');
        }
    } catch (error) {
        console.error('取消訂單失敗:', error);
        showErrorMessage('取消訂單失敗: ' + (error.message || '未知錯誤'));
    } finally {
        cancelLoading.value = false;
    }
};

// 載入訂單金流狀態
const loadPaymentStatus = async (orderId) => {
    try {
        const response = await getPaymentStatus(orderId);
        if (response && response.result) {
            paymentStatus.value = response.data;
            return response.data;
        } else {
            showErrorMessage(response?.message || '查詢金流狀態失敗');
            return null;
        }
    } catch (error) {
        console.error('查詢金流狀態失敗:', error);
        showErrorMessage('查詢金流狀態失敗: ' + (error.message || '未知錯誤'));
        return null;
    }
};

const toggleAdminInventoryItem = (itemId, invId, checked) => {
    if (!directRefundItems[itemId]) return;
    let ids = directRefundItems[itemId].user_inventory_item_ids || [];
    if (checked) {
        if (!ids.includes(invId)) ids.push(invId);
    } else {
        ids = ids.filter(id => id !== invId);
    }
    directRefundItems[itemId].user_inventory_item_ids = ids;
};

const buildAdminItemPackages = (item) => {
    const invs = item.inventory_items || [];
    if (invs.length === 0) return [];

    const orderItemQty = Math.max(1, item.qty || 1);

    const hasPackageInstanceIds = invs.some(inv => !!inv.package_instance_id);

    if (hasPackageInstanceIds) {
        const pkgMap = {};
        invs.forEach(inv => {
            const pkgId = inv.package_instance_id || 'LEGACY';
            if (!pkgMap[pkgId]) {
                pkgMap[pkgId] = [];
            }
            pkgMap[pkgId].push(inv);
        });

        const packages = [];
        let pIdx = 1;
        Object.keys(pkgMap).forEach(pkgId => {
            const pkgItems = pkgMap[pkgId];
            const isAvailable = pkgItems.every(i => i.is_available);
            packages.push({
                package_index: pIdx++,
                package_instance_id: pkgId,
                items: pkgItems,
                item_ids: pkgItems.map(i => i.id),
                is_available: isAvailable,
                selected: isAvailable
            });
        });

        packages.sort((a, b) => (b.is_available ? 1 : 0) - (a.is_available ? 1 : 0));
        packages.forEach((pkg, idx) => { pkg.package_index = idx + 1; });
        return packages;
    }

    const getGroupKey = (inv) => {
        if (inv.inventory_item_id) return inv.inventory_item_id;
        return (inv.name_snapshot || '').replace(/[\s\(\（].*?[\)\）]/g, '').replace(/套件[一二三四五六七八九十0-9]+/g, '').trim();
    };

    const groups = {};
    invs.forEach(inv => {
        const key = getGroupKey(inv);
        if (!groups[key]) groups[key] = [];
        groups[key].push(inv);
    });

    const groupKeys = Object.keys(groups);
    if (groupKeys.length === 0) return [];

    // 優先順序：可用 (權重 2) > 不可退款/已使用/不在買家手上 (權重 0)
    const getItemPriority = (inv) => {
        if (!inv.is_available) return 0;
        return 2;
    };

    groupKeys.forEach(k => {
        groups[k].sort((a, b) => {
            const prioA = getItemPriority(a);
            const prioB = getItemPriority(b);
            if (prioB !== prioA) return prioB - prioA;
            return (Number(a.id) || 0) - (Number(b.id) || 0);
        });
    });

    const ratioPerPkg = {};
    groupKeys.forEach(k => {
        ratioPerPkg[k] = Math.max(1, Math.round(groups[k].length / orderItemQty));
    });

    const packages = [];
    for (let p = 0; p < orderItemQty; p++) {
        const pkgItems = [];
        let isAvailable = true;

        groupKeys.forEach(k => {
            const ratio = ratioPerPkg[k];
            const startIdx = p * ratio;
            for (let r = 0; r < ratio; r++) {
                const invItem = groups[k][startIdx + r];
                if (invItem) {
                    pkgItems.push(invItem);
                    if (!invItem.is_available) {
                        isAvailable = false;
                    }
                } else {
                    isAvailable = false;
                }
            }
        });

        if (pkgItems.length > 0) {
            packages.push({
                package_index: p + 1,
                items: pkgItems,
                is_available: isAvailable,
                selected: isAvailable,
                item_ids: pkgItems.map(i => i.id)
            });
        }
    }

    return packages;
};

const syncAdminItemFromPackages = (itemId) => {
    const itemObj = directRefundItems[itemId];
    if (!itemObj) return;
    const orderItem = refundOrderData.value?.items?.find(i => i.id == itemId);
    if (!orderItem || !orderItem.packages) return;

    const selectedPkgs = orderItem.packages.filter(p => p.selected && p.is_available);
    itemObj.qty = selectedPkgs.length;
    const allSelectedIds = [];
    selectedPkgs.forEach(p => {
        allSelectedIds.push(...p.item_ids);
    });
    itemObj.user_inventory_item_ids = allSelectedIds;
};

// 退款
const handleRefundOrder = async (record) => {
    if (!canRefundOrder.value) {
        message.warning('您沒有訂單退款的權限');
        return;
    }

    directRefundForm.fee_amount = 0;
    directRefundForm.fee_note = '';
    directRefundForm.reason = '';

    // 載入訂單完整資料
    try {
        refundLoading.value = true;
        const orderResponse = await getShopOrder(record.id);
        if (orderResponse && orderResponse.result) {
            refundOrderData.value = orderResponse.data;
            if (refundOrderData.value?.items) {
                refundOrderData.value.items.forEach(item => {
                    const available = Math.max(0, item.qty - (item.refunded_qty || 0));
                    const invItems = (item.inventory_items || []).map(inv => ({
                        id: inv.id,
                        inventory_item_id: inv.inventory_item_id,
                        package_instance_id: inv.package_instance_id,
                        name_snapshot: inv.name_snapshot,
                        status: inv.status,
                        status_label: inv.status_label || inv.status,
                        is_available: inv.is_available ?? (inv.status === 'available')
                    }));

                    item.packages = buildAdminItemPackages({ inventory_items: invItems, qty: available });
                    const hasPackages = item.packages && item.packages.length > 0;
                    const selectedPkgs = (item.packages || []).filter(p => p.selected && p.is_available);
                    const initialItemIds = [];
                    selectedPkgs.forEach(p => initialItemIds.push(...p.item_ids));

                    directRefundItems[item.id] = {
                        selected: available > 0,
                        qty: hasPackages ? selectedPkgs.length : available,
                        user_inventory_item_ids: initialItemIds
                    };
                });
            }
        } else {
            showErrorMessage('載入訂單資料失敗');
            return;
        }

        // 查詢金流狀態（bank_transfer 不需要呼叫 status API）
        const paymentDisplay = getOrderPaymentDisplay(orderResponse.data);
        if (paymentDisplay.gateway !== 'bank_transfer') {
            await loadPaymentStatus(record.id);
            if (paymentStatus.value?.can_cancel_auth) {
                directRefundForm.fee_amount = 0;
                directRefundForm.fee_note = '';
                // 授權尚未請款：金流限制只能全額取消授權，強制全選所有商品與套件
                if (refundOrderData.value?.items) {
                    refundOrderData.value.items.forEach(item => {
                        if (directRefundItems[item.id]) {
                            directRefundItems[item.id].selected = true;
                            directRefundItems[item.id].qty = item.qty;
                        }
                        if (item.packages) {
                            item.packages.forEach(pkg => {
                                pkg.selected = pkg.is_available;
                            });
                            const selectedPkgs = item.packages.filter(p => p.selected && p.is_available);
                            if (directRefundItems[item.id]) {
                                directRefundItems[item.id].qty = selectedPkgs.length;
                            }
                        }
                    });
                }
            }
        } else {
            // bank_transfer 不需要 paymentStatus，設為 null
            paymentStatus.value = null;
        }

        refundModalVisible.value = true;
    } catch (error) {
        console.error('載入訂單資料失敗:', error);
        showErrorMessage('載入訂單資料失敗: ' + (error.message || '未知錯誤'));
    } finally {
        refundLoading.value = false;
    }
};

// 取得退款 Modal 提示類型
const getRefundAlertType = () => {
    if (!refundOrderData.value) return 'info';
    const paymentDisplay = getOrderPaymentDisplay(refundOrderData.value);
    const isBankTransfer = paymentDisplay.gateway === 'bank_transfer';

    if (isBankTransfer) {
        return 'info';
    }
    const status = paymentStatus.value?.newebpay_status;
    if (status === 'cancelled' || status === 'refunded') {
        return 'error';
    }
    return paymentStatus.value?.can_cancel_auth ? 'warning' : 'info';
};

// 檢查是否有不可退款的套件/物品
const hasUnavailablePackage = computed(() => {
    if (!refundOrderData.value?.items) return false;
    return refundOrderData.value.items.some(item => {
        return (item.packages || []).some(pkg => !pkg.is_available);
    });
});

/** 與前台／後端 NewebPayGateway::shortHint 對齊的短文案 */
const refundItemsHint = computed(() => {
    if (paymentStatus.value?.can_cancel_auth) {
        return '尚未請款，僅能全額取消（取消授權）';
    }
    return '勾選要退的商品';
});

// 取得退款 Modal 提示訊息
const getRefundAlertMessage = () => {
    if (!refundOrderData.value) return '目前無法退款';
    const paymentDisplay = getOrderPaymentDisplay(refundOrderData.value);
    const isBankTransfer = paymentDisplay.gateway === 'bank_transfer';
    const isAtmMethod = ['atm', 'webatm', 'vacc'].includes((paymentDisplay.method || '').toLowerCase());

    if (isBankTransfer || isAtmMethod) {
        return '銀行／ATM／WebATM 無法線上退刷；請人工匯款後再點「確認退款」記錄（藍新仍會顯示已付款）。';
    }

    const status = paymentStatus.value?.newebpay_status;

    if (paymentStatus.value?.can_cancel_auth) {
        if (hasUnavailablePackage.value) {
            return '有物品已使用或轉讓，請待請款後再部分退款。';
        }
        return '尚未請款，僅能全額取消（取消授權），無法只退部分商品。';
    }
    if (paymentStatus.value?.can_refund) {
        return '已請款，可執行退款。';
    }
    if (status === 'cancelled') {
        return '此交易已取消授權，無法再申請退款。';
    }
    if (status === 'refunded') {
        return '此交易已退款，無法重複申請。';
    }
    return '目前無法取消授權或退款。';
};

// 判斷退款按鈕是否禁用
const isRefundButtonDisabled = () => {
    if (!refundOrderData.value) return true;
    const paymentDisplay = getOrderPaymentDisplay(refundOrderData.value);
    const isBankTransfer = paymentDisplay.gateway === 'bank_transfer';
    const isAtmMethod = ['atm', 'webatm', 'vacc'].includes((paymentDisplay.method || '').toLowerCase());

    if (isBankTransfer || isAtmMethod) {
        return false; // bank_transfer 或 ATM 人工退款直接允許
    }

    if (paymentStatus.value?.can_cancel_auth && hasUnavailablePackage.value) {
        return true; // 當授權尚未請款且有物品被轉讓/使用時，禁用全額取消授權按鈕
    }

    return (
        !paymentStatus.value ||
        (!paymentStatus.value.can_cancel_auth &&
            !paymentStatus.value.can_refund)
    );
};

// 取得退款按鈕文字
const getRefundButtonText = () => {
    if (!refundOrderData.value) return '確認退款';
    const paymentDisplay = getOrderPaymentDisplay(refundOrderData.value);
    const isBankTransfer = paymentDisplay.gateway === 'bank_transfer';
    const isAtmMethod = ['atm', 'webatm', 'vacc'].includes((paymentDisplay.method || '').toLowerCase());

    if (isBankTransfer || isAtmMethod) {
        return '確認退款（人工處理）';
    }

    return paymentStatus.value?.can_cancel_auth ? '確認取消授權' : '確認退款';
};

// 執行退款或取消授權
const handleConfirmRefund = async () => {
    if (!refundOrderData.value) return;

    const orderId = refundOrderData.value.id;
    const paymentDisplay = getOrderPaymentDisplay(refundOrderData.value);
    const isBankTransfer = paymentDisplay.gateway === 'bank_transfer';
    const isAtmMethod = ['atm', 'webatm', 'vacc'].includes((paymentDisplay.method || '').toLowerCase());

    // bank_transfer 或 ATM 轉帳不走信用卡 cancelAuth，直接進行人工退款流程
    const isCancelAuth = (isBankTransfer || isAtmMethod)
        ? false
        : paymentStatus.value?.can_cancel_auth === true;
    const isRefund = (isBankTransfer || isAtmMethod)
        ? true
        : paymentStatus.value?.can_refund === true;

    if (!isCancelAuth && !isRefund) {
        message.warning('此訂單目前無法進行退款或取消授權');
        return;
    }

    try {
        refundLoading.value = true;
        let response;

        if (isCancelAuth) {
            // 取消授權
            response = await cancelAuth(orderId);
            if (response && response.result) {
                message.success('取消授權處理中');
                refundModalVisible.value = false;
                loadOrders();
            } else {
                showErrorMessage(response?.message || '取消授權失敗');
            }
        } else if (isRefund) {
            const selectedItemsPayload = (refundOrderData.value?.items || [])
                .filter(item => directRefundItems[item.id]?.selected && directRefundItems[item.id]?.qty > 0)
                .map(item => {
                    const itemObj = directRefundItems[item.id];
                    const payload = {
                        order_item_id: item.id,
                        qty: itemObj.qty
                    };
                    if (itemObj.user_inventory_item_ids && itemObj.user_inventory_item_ids.length > 0) {
                        payload.user_inventory_item_ids = itemObj.user_inventory_item_ids;
                    }
                    return payload;
                });

            // 退款（帶入行政手續費、備註與部分退款商品品項）
            response = await refundOrder(orderId, {
                items: selectedItemsPayload,
                fee_amount: directRefundForm.fee_amount || 0,
                fee_note: directRefundForm.fee_note || undefined,
                reason: directRefundForm.reason || undefined,
                refund_amount: computedDirectActualRefund.value
            });
            if (response && response.result) {
                message.success('退款處理中');
                refundModalVisible.value = false;
                loadOrders();
            } else {
                showErrorMessage(response?.message || '退款失敗');
            }
        }
    } catch (error) {
        console.error('操作失敗:', error);
        showErrorMessage('操作失敗: ' + (error.message || '未知錯誤'));
    } finally {
        refundLoading.value = false;
    }
};

// 初始化
onMounted(() => {
    if (route.query.status) {
        filters.status = route.query.status;
    }
    if (route.query.order_no) {
        filters.order_no = String(route.query.order_no);
        filterCollapseActiveKey.value = ['filters'];
    }
    loadColumnSettings();
    loadOrders();
});
</script>

<style scoped>
.orders-list-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.orders-page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: rgba(255, 255, 255, 0.9);
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 24px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    opacity: 0.6;
}

/* ===== 搜尋與篩選區域 ===== */
.search-filter-section {
    margin-bottom: 32px;
    position: relative;
    z-index: 1;
}

.page-table-section {
    margin-top: 8px;
}

.orders-filter-collapse {
    background: transparent;
}

.orders-filter-collapse :deep(.ant-collapse-item) {
    border: 1px solid rgba(42, 74, 122, 0.25);
    border-radius: 12px;
}

.orders-filter-collapse :deep(.ant-collapse-header) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #64748b;
    padding: 12px 16px !important;
}

.orders-filter-header-title {
    font-family: 'Courier New', monospace;
}

.orders-filter-collapse :deep(.ant-collapse-content-box) {
    padding: 12px 16px 20px;
}

.orders-filter-body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.orders-filter-label {
    display: block;
    margin-bottom: 6px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.65);
}

.orders-filter-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 4px;
    border-top: 1px solid rgba(42, 74, 122, 0.12);
}

.orders-column-settings-btn {
    padding-inline: 8px;
    color: #8b5aad;
}

.order-quick-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
}

.order-quick-label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(42, 42, 58, 0.65);
    margin-right: 4px;
}

.amount-range-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.amount-range-inputs .amount-input {
    flex: 1;
    min-width: 0;
    width: auto;
}

.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: 0;
    flex-wrap: wrap;
    gap: 16px;
}

.search-box {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
}

.search-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
}

.w-60 {
    width: 240px;
    min-width: 200px;
}

.w-40 {
    width: 160px;
    min-width: 140px;
}

.amount-range-inputs {
    display: flex;
    align-items: center;
    gap: 8px;
}

.amount-input {
    width: 120px;
}

.amount-separator {
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

.toolbar-actions {
    flex-shrink: 0;
}

/* ===== 統一搜尋欄位樣式 ===== */
/* 訂單編號輸入框 */
.order-no-input :deep(.ant-input-wrapper) {
    border: none !important;
    box-shadow: none !important;
}

.order-no-input :deep(.ant-input) {
    background: rgba(255, 255, 255, 0.95) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    transition: all 0.3s ease !important;
}

.order-no-input :deep(.ant-input:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.order-no-input :deep(.ant-input:focus) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.order-no-input :deep(.ant-input::placeholder) {
    color: rgba(42, 42, 58, 0.5) !important;
    font-family: 'Courier New', monospace !important;
}

/* 下拉選單樣式 */
.status-select :deep(.ant-select-selector) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-select-selector:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-focused .ant-select-selector) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-select-selection-item),
.status-select :deep(.ant-select-selection-placeholder) {
    line-height: 30px !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace !important;
}

/* 日期選擇器樣式 */
.status-select :deep(.ant-picker) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-picker-focused) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

/* 數字輸入框樣式 */
.status-select :deep(.ant-input-number) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid rgba(200, 200, 210, 0.4) !important;
    border-radius: 8px !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    height: 32px !important;
    line-height: 30px !important;
    transition: all 0.3s ease !important;
}

.status-select :deep(.ant-input-number-input) {
    height: 30px !important;
    line-height: 30px !important;
    font-size: 13px !important;
}

.status-select :deep(.ant-input-number:hover) {
    border-color: rgba(42, 74, 122, 0.6) !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

.status-select :deep(.ant-input-number-focused) {
    border-color: rgba(42, 74, 122, 0.8) !important;
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2) !important;
}

/* ===== 表格內容樣式 ===== */
.order-no-link {
    color: #3b6ea5;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    font-size: 14px;
}

.order-no-link:hover {
    color: #9a8c98;
    text-decoration: underline;
}

.order-no-text {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
    font-size: 14px;
}

.payment-no-cell {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
}

.payment-no-copy-icon {
    color: #3b6ea5;
    cursor: pointer;
    font-size: 14px;
}

.payment-no-copy-icon:hover {
    color: #2a4a7a;
}

/* 會員連結 */
.user-link {
    color: #3b6ea5;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
}

.user-link:hover {
    color: #9a8c98;
    text-decoration: underline;
}

.user-name {
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
    font-weight: 500;
}

/* 狀態呈現 - 突出設計 */
.status-cell {
    display: flex;
    justify-content: center;
    align-items: center;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 10px;
    border-radius: 999px;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 12px;
    line-height: 1.35;
    border: 1px solid;
    transition: all 0.2s ease;
    white-space: nowrap;
}

.status-text {
    line-height: 1.4;
}

/* 不同狀態的樣式 */
.status-badge-pending {
    background: rgba(255, 168, 122, 0.1);
    border-color: #ffa87a;
    color: #ffa87a;
}

.status-badge-paid {
    background: rgba(91, 122, 138, 0.1);
    border-color: #5b7a8a;
    color: #5b7a8a;
}

.status-badge-completed {
    background: rgba(82, 196, 26, 0.1);
    border-color: #52c41a;
    color: #52c41a;
}

.status-badge-cancelled {
    background: rgba(166, 122, 122, 0.1);
    border-color: #a67a7a;
    color: #a67a7a;
}

.status-badge-refunding {
    background: rgba(184, 160, 90, 0.1);
    border-color: #b8a05a;
    color: #b8a05a;
}

.status-badge-refunded {
    background: rgba(42, 74, 122, 0.1);
    border-color: #2a4a7a;
    color: #2a4a7a;
}

.status-badge-partially-refunded {
    background: rgba(249, 115, 22, 0.1);
    border-color: #f97316;
    color: #f97316;
}

.status-badge-cancellation-pending {
    background: rgba(184, 160, 90, 0.1);
    border-color: #b8a05a;
    color: #b8a05a;
}

.status-badge-refund-failed {
    background: rgba(166, 122, 122, 0.1);
    border-color: #a67a7a;
    color: #a67a7a;
}

/* 匯款狀態 icon */
.payment-gateway-tag-with-icon {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.bank-transfer-icon {
    font-size: 12px;
}

.bank-transfer-icon-ok {
    color: #52c41a;
}

.bank-transfer-icon-missing {
    color: #faad14;
}

/* 欄位設置按鈕樣式（沿用會員列表的風格） */
.column-settings-icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 8px;
    border: 1px solid rgba(200, 200, 210, 0.4);
    background: rgba(255, 255, 255, 0.9);
    color: #2a2a3a;
    transition: all 0.3s ease;
}

.column-settings-icon-btn:hover {
    border-color: rgba(42, 74, 122, 0.6);
    color: #2a4a7a;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.column-settings-icon-btn :deep(.anticon) {
    font-size: 16px;
}

/* 訂單金額 */
.order-amount-cell {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    text-align: right;
    font-family: 'Courier New', monospace;
}

.amount-value {
    font-size: 15px;
    font-weight: 700;
    color: #2a2a3a;
    letter-spacing: 0.2px;
}

.amount-refund {
    font-size: 11px;
    font-weight: 700;
    color: #0891b2;
    line-height: 1.2;
}

/* 商品列表 */
.items-list {
    font-family: 'Noto Sans TC', sans-serif;
    color: #2a2a3a;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 2px 0;
}

.item-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: 6px;
    padding: 0;
}

.item-type-tag {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2.2rem;
    padding: 0.05rem 0.35rem;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: 0.02em;
}

.item-type-tag--course {
    color: #1e3a5f;
    background: #d5e6f3;
}

.item-type-tag--physical {
    color: #9a3412;
    background: #ffedd5;
}

.item-type-tag--virtual {
    color: #0369a1;
    background: #e0f2fe;
}

.item-type-tag--ticket {
    color: #047857;
    background: #d1fae5;
}

.item-type-tag--default {
    color: #475569;
    background: #f1f5f9;
}

.item-row:hover {
    background: transparent;
    border-color: transparent;
}

.item-name {
    font-size: 12px;
    font-weight: 500;
    color: #3a3a4a;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.45;
}

.item-qty {
    color: #8a8a9a;
    font-weight: 600;
    font-size: 12px;
    flex-shrink: 0;
}

.item-more {
    color: #8a8a9a;
    font-size: 11px;
    font-weight: 500;
    margin-top: 0;
    padding-left: 0;
    font-style: normal;
}

.item-empty {
    color: #9a9aaa;
    font-size: 12px;
    font-style: italic;
    padding-left: 0;
}

/* 時間 */
.time-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: 12px;
    line-height: 1.35;
    color: #4b5563;
}

.time-row {
    display: flex;
    align-items: baseline;
    gap: 6px;
    white-space: nowrap;
}

.time-label {
    flex: none;
    min-width: 2em;
    font-size: 10px;
    font-weight: 700;
    color: #9ca3af;
    letter-spacing: 0.04em;
}

.user-invoice-cell {
    display: flex;
    flex-direction: column;
    gap: 3px;
    font-size: 11px;
    line-height: 1.35;
    color: #374151;
    min-width: 0;
}

.user-invoice-type {
    display: inline-flex;
    align-self: flex-start;
    padding: 0.05rem 0.35rem;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 700;
    color: #475569;
    background: #f1f5f9;
}

.user-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    font-size: 12px;
    line-height: 1.35;
}

.user-referrer-line {
    font-size: 10px;
    color: #9ca3af;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-link--sub {
    font-size: 10px;
}

.user-invoice-type--company {
    color: #2a4a7a;
    background: #d5e6f3;
}

.user-invoice-line {
    line-height: 1.4;
}

.user-invoice-line--wrap {
    white-space: normal;
    word-break: break-word;
}

.user-invoice-line--muted {
    color: #9ca3af;
}

.user-invoice-line--mono {
    font-family: 'Courier New', monospace;
    font-size: 10px;
    letter-spacing: 0.02em;
}

/* 操作按鈕 */
.action-buttons {
    display: inline-flex;
    flex-wrap: nowrap;
    gap: 4px;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
}

.action-buttons :deep(.ant-btn) {
    margin: 0;
    padding-inline: 8px;
}

.action-btn-more :deep(.anticon) {
    margin-left: 2px;
    font-size: 10px;
}

.page-table-section :deep(.ant-table-measure-row),
.page-table-section :deep(tr.ant-table-measure-row) {
    height: 0 !important;
    visibility: collapse !important;
    pointer-events: none !important;
}

.page-table-section :deep(.ant-table-measure-row td) {
    height: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    line-height: 0 !important;
    font-size: 0 !important;
}

.action-btn-more {
    color: #8a8a9a;
}

/* 詳情 Modal 內容 */
.order-detail-content {
    position: relative;
    z-index: 1;
}

.detail-section {
    margin-bottom: 32px;
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
    text-shadow: 0 0 10px rgba(42, 74, 122, 0.3);
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.retry-refund-btn {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    border-radius: 8px;
}

:deep(.shipping-yes) {
    background: rgba(154, 180, 170, 0.25) !important;
    border: 1.5px solid rgba(120, 150, 140, 0.6) !important;
    color: #4a6a5f !important;
    font-weight: 600 !important;
    font-family: 'Courier New', monospace !important;
    padding: 4px 12px !important;
    border-radius: 6px !important;
}

:deep(.shipping-no) {
    background: rgba(200, 200, 200, 0.2) !important;
    border: 1.5px solid rgba(160, 160, 160, 0.4) !important;
    color: #6a6a7a !important;
    font-weight: 500 !important;
    font-family: 'Courier New', monospace !important;
    padding: 4px 12px !important;
    border-radius: 6px !important;
}

/* 訂單詳情 Modal 內的表格樣式 */
.order-detail-content :deep(.ant-table) {
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid rgba(138, 175, 255, 0.25);
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 4px 16px rgba(138, 175, 255, 0.15);
}

.order-detail-content :deep(.ant-table-thead > tr > th) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.15),
        rgba(42, 74, 122, 0.1)
    ) !important;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
    text-align: center !important;
}

.order-detail-content :deep(.ant-table-tbody > tr > td) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-bottom: 1px solid rgba(138, 175, 255, 0.15) !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
}

.order-detail-content :deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(138, 175, 255, 0.08) !important;
    border-bottom-color: rgba(138, 175, 255, 0.25) !important;
}

.order-detail-content :deep(.ant-table-tbody > tr:last-child > td) {
    border-bottom: none !important;
}

.order-detail-content :deep(.ant-table-bordered .ant-table-thead > tr > th),
.order-detail-content :deep(.ant-table-bordered .ant-table-tbody > tr > td) {
    border-right: 1px solid rgba(138, 175, 255, 0.2) !important;
}

.order-detail-content
    :deep(.ant-table-bordered .ant-table-thead > tr > th:last-child),
.order-detail-content
    :deep(.ant-table-bordered .ant-table-tbody > tr > td:last-child) {
    border-right: none !important;
}

/* Descriptions 樣式優化 */
.order-detail-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 12px;
    overflow: hidden;
    border: 2px solid rgba(138, 175, 255, 0.25);
    box-shadow: 0 4px 16px rgba(138, 175, 255, 0.15);
}

.order-detail-content :deep(.ant-descriptions-header) {
    margin-bottom: 0;
}

.order-detail-content :deep(.ant-descriptions-title) {
    font-family: 'Courier New', monospace;
    font-weight: 700;
    color: #2a2a3a;
}

.order-detail-content :deep(.ant-descriptions-item-label) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.12),
        rgba(42, 74, 122, 0.08)
    ) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 2px solid rgba(138, 175, 255, 0.2) !important;
    padding: 12px 16px !important;
    width: 140px;
}

.order-detail-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

.order-detail-content :deep(.ant-descriptions-row) {
    border-bottom: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.order-detail-content :deep(.ant-descriptions-row:last-child) {
    border-bottom: none !important;
}

/* 表格行 hover 狀態 - 主列表表格 */
:deep(.ant-table-tbody > tr:hover > td),
:deep(.ant-table-tbody > tr:hover),
:deep(.ant-table-tbody > tr:hover > th) {
    background: rgba(154, 140, 152, 0.05) !important;
    background-color: rgba(154, 140, 152, 0.05) !important;
}

/* 狀態欄位 hover 時保持不變 */
:deep(.ant-table-tbody > tr:hover > td.status-cell),
:deep(.ant-table-tbody > tr:hover > td .status-cell) {
    background: transparent !important;
    background-color: transparent !important;
}

:deep(.ant-table-tbody tr) {
    background: rgba(255, 255, 255, 0.6) !important;
    background-color: rgba(255, 255, 255, 0.6) !important;
}

:deep(.ant-table-tbody tr td) {
    background: rgba(255, 255, 255, 0.6) !important;
    background-color: rgba(255, 255, 255, 0.6) !important;
    padding: 14px 12px !important;
    vertical-align: middle !important;
}

:deep(.ant-table-thead > tr > th) {
    padding: 12px !important;
    white-space: nowrap;
}

.page-table-section :deep(.ant-table) {
    font-size: 13px;
}

/* Modal 確認視窗樣式 */
:deep(.ant-modal) {
    font-family: 'Courier New', monospace;
}

:deep(.ant-modal-content) {
    border-radius: 16px;
    overflow: hidden;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border: 2px solid transparent;
    position: relative;
    box-shadow: 0 8px 32px rgba(42, 74, 122, 0.2);
}

:deep(.ant-modal-content)::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
    pointer-events: none;
    z-index: -1;
}

:deep(.ant-modal-header) {
    background: transparent;
    border-bottom: 1px solid rgba(138, 175, 255, 0.15);
    padding: 20px 24px;
    border-radius: 16px 16px 0 0;
}

:deep(.ant-modal-title) {
    font-family: 'Courier New', monospace;
    font-weight: 700;
    font-size: 18px;
    color: #2a2a3a;
    display: flex;
    align-items: center;
    gap: 12px;
}

:deep(.ant-modal-body) {
    padding: 24px;
    font-family: 'Courier New', monospace;
    color: #2a2a3a;
    font-size: 14px;
    line-height: 1.6;
}

:deep(.ant-modal-body .anticon) {
    color: #ffa87a;
    font-size: 22px;
}

:deep(.ant-modal-footer) {
    border-top: 1px solid rgba(138, 175, 255, 0.15);
    padding: 16px 24px;
    background: rgba(248, 249, 250, 0.5);
}

:deep(.ant-modal-footer .ant-btn) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    border-radius: 8px;
    height: 36px;
    padding: 0 20px;
    transition: all 0.2s ease;
    border: 2px solid transparent;
}

:deep(.ant-modal-footer .ant-btn-default) {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(138, 175, 255, 0.3);
    color: #2a2a3a;
}

:deep(.ant-modal-footer .ant-btn-default:hover) {
    background: rgba(138, 175, 255, 0.1);
    border-color: rgba(138, 175, 255, 0.5);
    color: #3b6ea5;
}

:deep(.ant-modal-footer .ant-btn-dangerous) {
    background: rgba(255, 168, 122, 0.1);
    border-color: #ffa87a;
    color: #ffa87a;
}

:deep(.ant-modal-footer .ant-btn-dangerous:hover) {
    background: rgba(255, 168, 122, 0.2);
    border-color: #ffa87a;
    color: #ff8c5a;
    box-shadow: 0 0 12px rgba(255, 168, 122, 0.3);
}

:deep(.ant-modal-close) {
    color: #6a6a7a;
    transition: all 0.2s ease;
}

:deep(.ant-modal-close:hover) {
    color: #2a4a7a;
}

:deep(.ant-modal-mask) {
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
}

/* Tab 樣式 */
.order-detail-tabs {
    font-family: 'Courier New', monospace;
}

:deep(.order-detail-tabs .ant-tabs-nav) {
    margin-bottom: 24px;
}

:deep(.order-detail-tabs .ant-tabs-tab) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 14px;
    padding: 12px 20px;
    color: #6a6a7a;
    transition: all 0.2s ease;
}

:deep(.order-detail-tabs .ant-tabs-tab:hover) {
    color: #3b6ea5;
}

:deep(.order-detail-tabs .ant-tabs-tab-active) {
    color: #3b6ea5;
}

:deep(.order-detail-tabs .ant-tabs-ink-bar) {
    background: linear-gradient(90deg, #3b6ea5, #2a4a7a);
    height: 3px;
}

.tab-content {
    min-height: 200px;
}

/* 交易記錄樣式 */
.transaction-card {
    margin-bottom: 16px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(138, 175, 255, 0.2);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.2s ease;
}

.transaction-card:hover {
    border-color: rgba(138, 175, 255, 0.4);
    box-shadow: 0 4px 12px rgba(138, 175, 255, 0.15);
}

.transaction-header {
    padding: 16px 20px;
    background: rgba(248, 249, 250, 0.8);
    border-bottom: 1px solid rgba(138, 175, 255, 0.15);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.transaction-title {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.transaction-id {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    font-size: 14px;
    color: #2a2a3a;
}

.status-tag {
    font-family: 'Courier New', monospace;
    font-weight: 600;
}

.transaction-time {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #6a6a7a;
}

.transaction-content {
    padding: 20px;
}

.transaction-collapse {
    margin-bottom: 16px;
    background: transparent;
}

:deep(.transaction-collapse .ant-collapse-item) {
    border: 1px solid rgba(138, 175, 255, 0.2);
    border-radius: 8px;
    margin-bottom: 12px;
    background: rgba(255, 255, 255, 0.5);
}

:deep(.transaction-collapse .ant-collapse-header) {
    font-family: 'Courier New', monospace;
    font-weight: 600;
    color: #2a2a3a;
    padding: 12px 16px;
}

.collapse-header-with-copy {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 12px;
}

.copy-json-btn {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #3b6ea5;
    padding: 0 8px;
    height: auto;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: all 0.2s ease;
}

.copy-json-btn:hover {
    color: #2a4a7a;
    background: rgba(138, 175, 255, 0.1);
}

.copy-json-btn :deep(.anticon) {
    font-size: 14px;
}

:deep(.transaction-collapse .ant-collapse-content-box) {
    padding: 16px;
    background: rgba(248, 249, 250, 0.5);
}

.json-content {
    font-family: 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.6;
    color: #2a2a3a;
    background: rgba(255, 255, 255, 0.8);
    padding: 12px;
    border-radius: 6px;
    border: 1px solid rgba(138, 175, 255, 0.15);
    overflow-x: auto;
    margin: 0;
}

.transaction-info {
    margin-top: 16px;
}

.transaction-info :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.transaction-info :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.transaction-info :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 12px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #9a9aaa;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.empty-state p {
    margin: 0;
}

/* 積分抵扣樣式 */
.point-discount {
    color: #ffa87a;
    font-weight: 700;
    font-family: 'Courier New', monospace;
}

.point-discount-detail {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.point-discount-tag {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    padding: 4px 10px !important;
    border-radius: 6px !important;
    border: 1px solid rgba(168, 255, 246, 0.5) !important;
    background: rgba(168, 255, 246, 0.15) !important;
    color: #0d0d0d !important;
    box-shadow: 0 0 8px rgba(168, 255, 246, 0.25) !important;
}

.point-discount-tag:hover {
    border-color: rgba(168, 255, 246, 0.7) !important;
    box-shadow: 0 0 12px rgba(168, 255, 246, 0.35) !important;
}

/* 付款方式 Tag 樣式 */
.payment-method-tag,
.payment-gateway-tag {
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    padding: 4px 12px !important;
    border-radius: 6px !important;
}

.payment-cell {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px 6px;
}

.payment-account-inline {
    font-size: 12px;
    color: #6a6a7a;
    white-space: nowrap;
}

/* 支付記錄樣式 */
.payments-section {
    margin-bottom: 32px;
}

.payment-card {
    margin-bottom: 16px;
    background: rgba(255, 255, 255, 0.95);
    border: 2px solid rgba(138, 175, 255, 0.25);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 4px 16px rgba(138, 175, 255, 0.15);
    transition: all 0.2s ease;
}

.payment-card:hover {
    border-color: rgba(138, 175, 255, 0.4);
    box-shadow: 0 6px 20px rgba(138, 175, 255, 0.2);
}

.payment-card :deep(.ant-descriptions) {
    background: transparent;
    border: none;
    box-shadow: none;
}

.payment-card :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    color: #2a2a3a !important;
    border-right: 2px solid rgba(138, 175, 255, 0.2) !important;
    padding: 10px 14px !important;
}

.payment-card :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 12px !important;
    color: #2a2a3a !important;
    padding: 10px 14px !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

.payment-transactions {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px solid rgba(138, 175, 255, 0.15);
}

.transactions-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.no-transactions {
    text-align: center;
    padding: 20px;
    color: #9a9aaa;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    font-style: italic;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .search-row {
        flex-direction: column;
    }

    .w-60,
    .w-40 {
        width: 100%;
        min-width: 100%;
    }

    .toolbar-actions {
        width: 100%;
        justify-content: flex-start;
    }
}

/* 退款 Modal 樣式 */
.refund-modal-content {
    font-family: inherit;
}

.refund-order-info,
.refund-payment-status {
    margin-bottom: 16px;
}

.refund-settings {
    margin-top: 4px;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
}

.refund-section-title {
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    margin: 0 0 10px;
}

.refund-amount {
    font-size: 16px;
    font-weight: 700;
    color: #7c5cbf;
}

.refund-trade-no {
    word-break: break-all;
    font-variant-numeric: tabular-nums;
}

.refund-items {
    margin-bottom: 12px;
    border: 1px solid #e8e8ef;
    border-radius: 8px;
    padding: 10px 12px;
    background: #fafafa;
}

.refund-items__hint {
    margin: 0 0 8px;
    font-size: 12px;
    color: #64748b;
}

.refund-item {
    padding: 8px 0;
    border-bottom: 1px solid #eee;
}

.refund-item:last-child {
    border-bottom: none;
    padding-bottom: 0;
}

.refund-item__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px 12px;
}

.refund-item__name {
    font-weight: 600;
    color: #1e293b;
}

.refund-item__price {
    margin-left: 6px;
    color: #64748b;
    font-weight: 500;
}

.refund-item__qty {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #64748b;
    white-space: nowrap;
}

.refund-item__qty-input {
    width: 72px;
}

.refund-item__qty-max {
    color: #94a3b8;
}

.refund-packages {
    margin: 8px 0 0 24px;
    padding: 8px 10px;
    background: #fff;
    border: 1px solid #eee;
    border-radius: 6px;
}

.refund-packages__hint {
    margin: 0 0 6px;
    font-size: 12px;
    color: #94a3b8;
}

.refund-package {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 6px 10px;
    padding: 4px 0;
    font-size: 12px;
}

.refund-package--disabled {
    color: #94a3b8;
    text-decoration: line-through;
}

.refund-form {
    margin-top: 4px;
}

.refund-summary-box {
    padding: 10px 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
}

.refund-summary-box__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}

.refund-summary-box__sub {
    margin-top: 4px;
    font-size: 12px;
    color: #a16207;
}

.refund-label {
    font-weight: 600;
    color: #475569;
}

.refund-value {
    font-size: 16px;
    font-weight: 700;
    color: #0e7490;
}

.refund-action-hint {
    margin-top: 14px;
}

.refund-modal-content :deep(.ant-descriptions) {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
}

.refund-modal-content :deep(.ant-descriptions-item-label) {
    background: #f8fafc !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #475569 !important;
    width: 96px;
}

.refund-modal-content :deep(.ant-descriptions-item-content) {
    font-size: 13px !important;
    color: #1e293b !important;
}

/* 標記已完成 Modal 樣式 */
.complete-modal-content {
    font-family: 'Courier New', monospace;
}

.complete-order-info,
.complete-order-items {
    margin-bottom: 24px;
}

.complete-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.complete-amount {
    font-size: 18px;
    font-weight: 700;
    color: #2a4a7a;
}

.complete-modal-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.complete-modal-content :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.complete-modal-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

.complete-modal-content :deep(.ant-table) {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
    background: rgba(255, 255, 255, 0.95);
}

.complete-modal-content :deep(.ant-table-thead > tr > th) {
    background: linear-gradient(
        135deg,
        rgba(138, 175, 255, 0.15),
        rgba(42, 74, 122, 0.1)
    ) !important;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
    text-align: center !important;
}

.complete-modal-content :deep(.ant-table-tbody > tr > td) {
    background: rgba(255, 255, 255, 0.9) !important;
    border-bottom: 1px solid rgba(138, 175, 255, 0.15) !important;
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    padding: 12px 16px !important;
}

.complete-modal-content :deep(.ant-table-tbody > tr:hover > td) {
    background: rgba(138, 175, 255, 0.08) !important;
    border-bottom-color: rgba(138, 175, 255, 0.25) !important;
}

/* 標記已付款 Modal 樣式 */
.paid-modal-content {
    font-family: 'Courier New', monospace;
}

.paid-order-info {
    margin-bottom: 24px;
}

.paid-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.paid-amount {
    font-size: 18px;
    font-weight: 700;
    color: #2a4a7a;
}

.paid-modal-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.paid-modal-content :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.paid-modal-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}

/* 取消訂單 Modal 樣式 */
.cancel-modal-content {
    font-family: 'Courier New', monospace;
}

.cancel-order-info {
    margin-bottom: 24px;
}

.cancel-section-title {
    font-size: 16px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 2px solid rgba(138, 175, 255, 0.3);
}

.cancel-amount {
    font-size: 18px;
    font-weight: 700;
    color: #2a4a7a;
}

.cancel-action-hint {
    margin-top: 20px;
}

.cancel-modal-content :deep(.ant-descriptions) {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(138, 175, 255, 0.2);
}

.cancel-modal-content :deep(.ant-descriptions-item-label) {
    background: rgba(138, 175, 255, 0.1) !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    border-right: 1px solid rgba(138, 175, 255, 0.15) !important;
}

.cancel-modal-content :deep(.ant-descriptions-item-content) {
    font-family: 'Courier New', monospace !important;
    font-size: 13px !important;
    color: #2a2a3a !important;
    background: rgba(255, 255, 255, 0.6) !important;
}
</style>

<style>
/* Modal 掛在 body，需非 scoped */
.refund-confirm-modal .ant-modal {
    max-width: calc(100vw - 32px);
}

.refund-confirm-modal .ant-modal-body {
    max-height: min(70vh, 720px);
    overflow-y: auto;
}
</style>
